/**
 * English translations (default locale)
 *
 * This is the source of truth for all translatable strings.
 * Other locale files must satisfy `typeof en` for compile-time safety.
 *
 * Key naming convention: domain.component.element
 */
const en = {
  // ── Error messages ──────────────────────────────────────────────
  'error.invalid_api_key.title': 'Invalid API Key',
  'error.invalid_api_key.message': 'The API key you provided is not valid. Please check your key and try again.',
  'error.invalid_api_key.action.settings': 'Open Settings',
  'error.invalid_api_key.action.retry': 'Retry',
  'error.invalid_credentials.title': 'Invalid Credentials',
  'error.invalid_credentials.message': 'Your credentials could not be verified. Please re-authenticate.',
  'error.invalid_credentials.action.settings': 'Open Settings',
  'error.expired_oauth_token.title': 'Session Expired',
  'error.expired_oauth_token.message': 'Your authentication session has expired. Please sign in again.',
  'error.expired_oauth_token.action.reauth': 'Sign In Again',
  'error.token_expired.title': 'Token Expired',
  'error.token_expired.message': 'Your workspace token has expired. Please re-authenticate.',
  'error.token_expired.action.reauth': 'Re-authenticate',
  'error.rate_limited.title': 'Rate Limited',
  'error.rate_limited.message': 'Too many requests. Please wait a moment and try again.',
  'error.rate_limited.action.retry': 'Retry',
  'error.service_error.title': 'Service Error',
  'error.service_error.message': 'The AI service encountered an internal error. This is usually temporary.',
  'error.service_error.action.retry': 'Retry',
  'error.service_unavailable.title': 'Service Unavailable',
  'error.service_unavailable.message': 'The AI service is temporarily unavailable. Please try again later.',
  'error.service_unavailable.action.retry': 'Retry',
  'error.network_error.title': 'Network Error',
  'error.network_error.message': 'Could not connect to the server. Check your internet connection.',
  'error.network_error.action.retry': 'Retry',
  'error.proxy_error.title': 'Proxy Error',
  'error.proxy_error.message': 'The request was intercepted by a proxy or firewall.',
  'error.proxy_error.action.retry': 'Retry',
  'error.provider_timeout.title': 'Provider Timeout',
  'error.provider_timeout.message': 'The AI provider did not respond before the request timed out.',
  'error.provider_timeout.action.retry': 'Retry',
  'error.mcp_auth_required.title': 'Authentication Required',
  'error.mcp_auth_required.message': 'This MCP server requires authentication.',
  'error.mcp_auth_required.action.settings': 'Configure Source',
  'error.mcp_unreachable.title': 'MCP Server Unreachable',
  'error.mcp_unreachable.message': 'Could not connect to the MCP server. Check the server configuration.',
  'error.mcp_unreachable.action.settings': 'Configure Source',
  'error.billing_error.title': 'Billing Error',
  'error.billing_error.message': 'There is a billing issue with your account. Please check your payment details.',
  'error.billing_error.action.settings': 'Open Settings',
  'error.model_no_tool_support.title': 'Model Limitation',
  'error.model_no_tool_support.message': 'This model does not support tool/function calling.',
  'error.model_no_tool_support.action.settings': 'Change Model',
  'error.invalid_model.title': 'Invalid Model',
  'error.invalid_model.message': 'The specified model ID was not found.',
  'error.invalid_model.action.settings': 'Change Model',
  'error.data_policy_error.title': 'Data Policy Error',
  'error.data_policy_error.message': 'The request was blocked due to data policy restrictions.',
  'error.data_policy_error.action.retry': 'Retry',
  'error.invalid_request.title': 'Invalid Request',
  'error.invalid_request.message': 'The request was rejected by the API.',
  'error.invalid_request.action.retry': 'Retry',
  'error.image_too_large.title': 'Image Too Large',
  'error.image_too_large.message': 'The image exceeds the maximum allowed dimensions or file size.',
  'error.image_too_large.action.retry': 'Retry with smaller image',
  'error.provider_error.title': 'Provider Error',
  'error.provider_error.message': 'The AI provider is experiencing issues. Please try again later.',
  'error.provider_error.action.retry': 'Retry',
  'error.unknown_error.title': 'Unknown Error',
  'error.unknown_error.message': 'An unexpected error occurred.',
  'error.unknown_error.action.retry': 'Retry',

  // ── Permission modes ────────────────────────────────────────────
  'mode.safe.displayName': 'Explore',
  'mode.safe.shortName': 'Explore',
  'mode.safe.description': 'Read-only exploration. Blocks writes, never prompts.',
  'mode.ask.displayName': 'Ask to Edit',
  'mode.ask.shortName': 'Ask',
  'mode.ask.description': 'Prompts before making edits.',
  'mode.allow-all.displayName': 'Execute',
  'mode.allow-all.shortName': 'Execute',
  'mode.allow-all.description': 'Automatic execution, no prompts.',

  // ── Menu items ──────────────────────────────────────────────────
  'menu.app.about': 'About OKClaw Pro',
  'menu.app.settings': 'Settings...',
  'menu.app.hide': 'Hide OKClaw Pro',
  'menu.app.hideOthers': 'Hide Others',
  'menu.app.showAll': 'Show All',
  'menu.app.quit': 'Quit OKClaw Pro',
  'menu.app.installUpdate': 'Install Update...',
  'menu.app.checkForUpdates': 'Check for Updates...',
  'menu.file': 'File',
  'menu.file.newChat': 'New Chat',
  'menu.file.newWindow': 'New Window',
  'menu.edit': 'Edit',
  'menu.edit.undo': 'Undo',
  'menu.edit.redo': 'Redo',
  'menu.edit.cut': 'Cut',
  'menu.edit.copy': 'Copy',
  'menu.edit.paste': 'Paste',
  'menu.edit.selectAll': 'Select All',
  'menu.view': 'View',
  'menu.view.zoomIn': 'Zoom In',
  'menu.view.zoomOut': 'Zoom Out',
  'menu.view.resetZoom': 'Reset Zoom',
  'menu.view.toggleFocusMode': 'Toggle Focus Mode',
  'menu.view.toggleSidebar': 'Toggle Sidebar',
  'menu.view.toggleDevTools': 'Toggle Developer Tools',
  'menu.view.reload': 'Reload',
  'menu.view.forceReload': 'Force Reload',
  'menu.window': 'Window',
  'menu.window.minimize': 'Minimize',
  'menu.window.maximize': 'Maximize',
  'menu.window.zoom': 'Zoom',
  'menu.window.close': 'Close',
  'menu.help': 'Help',
  'menu.help.documentation': 'Help & Documentation',
  'menu.help.keyboardShortcuts': 'Keyboard Shortcuts',
  'menu.help.automations': 'Automations',
  'menu.debug': 'Debug',
  'menu.debug.checkForUpdates': 'Check for Updates',
  'menu.debug.installUpdate': 'Install Update',
  'menu.debug.toggleDevTools': 'Toggle DevTools',
  'menu.debug.resetToDefaults': 'Reset to Defaults...',

  // ── UI common ───────────────────────────────────────────────────
  'ui.copy': 'Copy',
  'ui.copied': 'Copied!',
  'ui.delete': 'Delete',
  'ui.cancel': 'Cancel',
  'ui.save': 'Save',
  'ui.edit': 'Edit',
  'ui.close': 'Close',
  'ui.open': 'Open',
  'ui.remove': 'Remove',
  'ui.continue': 'Continue',
  'ui.loading': 'Loading',
  'ui.retry': 'Retry',
  'ui.yes': 'Yes',
  'ui.no': 'No',
  'ui.back': 'Back',
  'ui.forward': 'Forward',
  'ui.search': 'Search',
  'ui.done': 'Done',
  'ui.dismiss': 'Dismiss',
  'ui.clear': 'Clear',
  'ui.confirm': 'Confirm',
  'ui.rename': 'Rename',
  'ui.add': 'Add',
  'ui.create': 'Create',
  'ui.failed': 'Failed',
  'ui.editing': 'Editing...',
  'ui.filterChats': 'Filter Chats',
  'ui.closeSearch': 'Close search',
  'ui.selectConversation': 'Select a conversation to get started',
  'ui.sendDisabled': 'Sending is currently disabled for this session.',
  'ui.openInNewWindow': 'Open in new window',
  'ui.copyPayload': 'Copy payload',
  'ui.error': 'Error',
  'ui.unknownError': 'Unknown error',
  'ui.showTechnicalDetails': 'Show technical details',
  'ui.hideTechnicalDetails': 'Hide technical details',

  // Agent error messages (i18n keys used in claude-sdk-error-mapper)
  'error.tokenExpired': 'Token expired, refreshing session...',
  'error.authFailed.title': 'Authentication Failed',
  'error.authFailed.message': 'Authentication Failed: Unable to authenticate. Your API key may be invalid or expired.',
  'error.authFailed.checkKey': 'Check your API key in settings',
  'error.authFailed.keyRevoked': 'Ensure your API key has not been revoked',
  'error.billing.title': 'Billing Error',
  'error.billing.message': 'Your account has a billing issue.',
  'error.billing.checkStatus': 'Check your account billing status',
  'error.rateLimit.title': 'Rate Limit Exceeded',
  'error.rateLimit.message': 'Too many requests. Please wait a moment before trying again.',
  'error.rateLimit.resetSoon': 'Rate limits reset after a short period',
  'error.rateLimit.upgradePlan': 'Consider upgrading your plan for higher limits',
  'error.invalidRequest.title': 'Invalid Request',
  'error.invalidRequest.message': 'The API rejected this request.',
  'error.invalidRequest.removeAttachments': 'Try removing any attachments and resending',
  'error.invalidRequest.checkImageFormat': 'Check if images are in a supported format (PNG, JPEG, GIF, WebP)',
  'error.outputTooLarge.title': 'Output Too Large',
  'error.outputTooLarge.message': 'The response exceeded the maximum output token limit.',
  'error.outputTooLarge.breakTask': 'Try breaking the task into smaller parts',
  'error.outputTooLarge.reduceScope': 'Reduce the scope of the request',
  'error.invalidApiKey.title': 'Invalid API Key',
  'error.invalidApiKey.message': 'Your API key was rejected. It may be invalid or expired.',
  'error.invalidCredentials.title': 'Invalid Credentials',
  'error.invalidCredentials.message': 'Your API key or OAuth token is missing or invalid.',
  'error.sessionExpired.title': 'Session Expired',
  'error.sessionExpired.message': 'Your session has expired. Please try signing in again.',
  'error.workspaceSessionExpired.title': 'Workspace Session Expired',
  'error.workspaceSessionExpired.message': 'Your workspace authentication has expired. Please re-authenticate.',
  'error.serviceError.title': 'Service Error',
  'error.serviceError.message': 'The AI service is temporarily unavailable.',
  'error.serviceUnavailable.title': 'Service Unavailable',
  'error.serviceUnavailable.message': 'The AI service is experiencing issues. Try again in a moment.',
  'error.missingGitBash.title': 'Git Bash Required',
  'error.missingGitBash.message': 'OKClaw Pro on Windows requires Git Bash. Please install Git for Windows from https://git-scm.com/downloads/win and restart the app.',
  'error.proxy.title': 'Network Proxy Error',
  'error.proxy.message': 'A proxy, firewall, or captive portal intercepted the API request.',
  'error.providerTimeout.title': 'Model Service Timed Out',
  'error.providerTimeout.message': 'The model service did not respond in time. Retry, switch to another model, or split large tasks into smaller requests.',
  'error.mcpAuth.title': 'Workspace Authentication Required',
  'error.mcpAuth.message': 'Your workspace connection needs to be re-authenticated.',
  'error.mcpUnreachable.title': 'MCP Server Unreachable',
  'error.mcpUnreachable.message': 'Cannot connect to the MCP server. Check your network connection.',
  'error.noToolSupport.title': 'Model Does Not Support Tools',
  'error.noToolSupport.message': 'The selected model does not support tool/function calling. Please choose a different model.',
  'error.invalidModel.title': 'Invalid Model',
  'error.invalidModel.message': 'The selected model was not found. Please check your model configuration.',
  'error.providerIssue.title': 'AI Provider Issue',
  'error.providerIssue.message': 'The AI provider may be experiencing temporary issues. Please retry in a moment.',
  'error.providerIssue.credentialsOk': 'Your credentials and local setup may still be correct.',
  'error.network.title': 'Connection Error',
  'error.network.message': 'Unable to connect to the API server. Check your internet connection.',
  'error.network.checkConnection': 'Verify your network connection is active',
  'error.network.firewallVpn': 'Firewall or VPN may be blocking the connection',
  'ui.searchLoading': 'Loading…',
  'ui.searchUnavailable': 'Search unavailable',
  'ui.searchTitlesOnly': 'Title search only',
  'ui.results': 'results',
  'ui.noSourcesConfigured': 'No sources configured',

  // ── Chat interface ──────────────────────────────────────────────
  'chat.acceptPlan': 'Accept Plan',
  'chat.accept': 'Accept',
  'chat.acceptAndCompact': 'Accept & Compact',
  'chat.acceptAndSendFollowups': 'Accept & Send Follow-ups',
  'chat.executePlanImmediately': 'Execute the plan immediately',
  'chat.worksForComplexPlans': 'Works best for complex, longer plans',
  'chat.viewFileChanges': 'View file changes',
  'chat.viewTurnDetails': 'View turn details',
  'chat.followUp': 'Follow up',
  'chat.followUpPlaceholder': 'Add comments the agent should consider in the next turn...',
  'chat.saveAndSend': 'Save & Send',
  'chat.moreSubmitActions': 'More submit actions',
  'chat.inputFailedTitle': 'Chat input failed to load',
  'chat.inputFailedDescription': 'The current draft may be malformed. Retry, clear the draft, or reload the window.',
  'chat.clearDraft': 'Clear Draft',
  'chat.reload': 'Reload',
  'chat.contextBadge': 'Context badge',
  'chat.branchOptions': 'Branch options',
  'chat.branch': 'Branch',
  'chat.branchFromThisMessage': 'Branch From This Message',
  'chat.branchFromThisMessageDescription': "Explore an alternate direction without disrupting this conversation's flow.",
  'chat.branchSessionName': 'Branch of {name}',
  'chat.renameSession': 'Rename Session',
  'chat.enterSessionName': 'Enter session name...',
  'chat.session': 'Session',
  'chat.delegationWaiting': 'Waiting for delegated sessions ({completed}/{total})',
  'chat.delegationRunningCount': '{count} running',

  // ── Code & Diff viewer ─────────────────────────────────────────
  'codeViewer.copyCode': 'Copy code',
  'codeViewer.switchToSplit': 'Switch to split view',
  'codeViewer.switchToUnified': 'Switch to unified view',
  'codeViewer.enableBackground': 'Enable background highlighting',
  'codeViewer.disableBackground': 'Disable background highlighting',
  'codeViewer.original': 'Original',
  'codeViewer.modified': 'Modified',
  'codeViewer.command': 'Command',
  'codeViewer.output': 'Output',
  'codeViewer.markdown': 'Markdown',

  // ── Table export ────────────────────────────────────────────────
  'table.copyAsMarkdown': 'Copy as Markdown',
  'table.copyAsCsv': 'Copy as CSV',
  'table.downloadAsXlsx': 'Download as XLSX',
  'table.exportTable': 'Export table',

  // ── Overlay / Preview ───────────────────────────────────────────
  'overlay.close': 'Close (Esc)',
  'overlay.viewFullscreen': 'View Fullscreen',
  'overlay.revealInFinder': 'Reveal in {fileManager}',
  'overlay.previousItem': 'Previous item',
  'overlay.nextItem': 'Next item',
  'overlay.selectItem': 'Select item',
  'overlay.zoomIn': 'Zoom in',
  'overlay.zoomOut': 'Zoom out',
  'overlay.resetZoom': 'Reset zoom',
  'overlay.zoomPresets': 'Zoom presets',
  'overlay.zoomToFit': 'Zoom to Fit',
  'overlay.loadingImage': 'Loading image...',
  'overlay.loadFailed': 'Load Failed',
  'overlay.copyAll': 'Copy all',
  'overlay.zoomInShortcut': 'Zoom in (\u2318+)',
  'overlay.zoomOutShortcut': 'Zoom out (\u2318\u2212)',
  'overlay.resetZoomShortcut': 'Reset zoom (\u23180)',

  // ── Browser controls ────────────────────────────────────────────
  'browser.stopLoading': 'Stop loading',
  'browser.reload': 'Reload',
  'browser.back': 'Back',
  'browser.forward': 'Forward',
  'browser.urlPlaceholder': 'Enter URL or search...',
  'browser.showWindow': 'Show Browser Window',
  'browser.openSessionUsing': 'Open Session Using this Window',
  'browser.openSessionWhichUsed': 'Open Session Which Used this Window',
  'browser.terminate': 'Terminate Browser',
  'browser.localFile': 'Local File',

  // ── Settings pages ──────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.preferences': 'Preferences',
  'settings.preferences.description': 'User preferences',
  'settings.account': 'Account',
  'settings.account.description': 'Profile, balance, and security',
  'settings.appearance': 'Appearance',
  'settings.appearance.description': 'Theme, font, tool icons',
  'settings.connections': 'Connections',
  'settings.connections.description': 'AI providers and API keys',
  'settings.language': 'Language',
  'settings.language.label': 'Language',
  'settings.language.description': 'Choose the display language for the interface',
  'settings.app': 'App',
  'settings.app.description': 'Notifications and updates',
  'settings.ai': 'AI',
  'settings.ai.description': 'Model, thinking, connections',
  'settings.input': 'Input',
  'settings.input.description': 'Send key, spell check',
  'settings.workspace': 'Workspace',
  'settings.workspace.description': 'Name, icon, working directory',
  'settings.permissions': 'Permissions',
  'settings.permissions.description': 'Explore mode rules',
  'settings.labels': 'Labels',
  'settings.labels.description': 'Manage session labels',
  'settings.tools': 'Tools',
  'settings.tools.description': 'External integrations and tools',
  'settings.server': 'Remote Access',
  'settings.server.description': 'Access and use OKClaw remotely from another device',
  'settings.sync': 'Sync & Backup',
  'settings.sync.description': 'WebDAV sync, backup, and restore',
  'settings.mailTitle': 'Email',
  'settings.mailDesc': 'Configure email sending for agent notifications',
  'settings.mailEnabled': 'Enable Email',
  'settings.mailEnabledDesc': 'Send emails when agents complete tasks or need attention',
  'settings.mailHost': 'SMTP Host',
  'settings.mailHostDesc': 'Your email provider\'s SMTP server address',
  'settings.mailHostPlaceholder': 'smtp.example.com',
  'settings.mailPort': 'SMTP Port',
  'settings.mailPortDesc': 'Usually 465 (SSL) or 587 (TLS)',
  'settings.mailSecure': 'Use SSL/TLS',
  'settings.mailSecureDesc': 'Enable secure connection (recommended)',
  'settings.mailUsername': 'Username',
  'settings.mailUsernameDesc': 'Your email address or app username',
  'settings.mailUsernamePlaceholder': 'your@email.com',
  'settings.mailPassword': 'Password',
  'settings.mailPasswordDesc': 'App password or SMTP authentication password',
  'settings.mailPasswordKeepDesc': 'Leave empty to keep current password',
  'settings.mailPasswordPlaceholder': 'Enter app password',
  'settings.mailFromName': 'From Name',
  'settings.mailFromNameDesc': 'Name shown as the sender',
  'settings.mailFromNamePlaceholder': 'OKClaw Pro',
  'settings.mailFromEmail': 'From Email',
  'settings.mailFromEmailDesc': 'Email address to send from',
  'settings.mailFromEmailPlaceholder': 'noreply@example.com',
  'settings.mailReplyTo': 'Reply-To',
  'settings.mailReplyToDesc': 'Email address for replies (optional)',
  'settings.mailReplyToPlaceholder': 'reply@example.com',
  'settings.mailTest': 'Test Connection',
  'settings.mailTestSuccess': 'Test Successful',
  'settings.mailTestFailed': 'Test Failed',
  'settings.mailSaved': 'Settings saved',
  'settings.mailSaveFailed': 'Failed to save settings',
  'settings.feishu': 'Feishu',
  'settings.feishuDesc': 'Feishu/Lark integration for team collaboration',
  // Feishu tabs
  'settings.feishuTabConfig': 'Bot Config',
  'settings.feishuTabBindings': 'Bindings',
  // Feishu status
  'settings.feishuStatusDisconnected': 'Disconnected',
  'settings.feishuStatusConnecting': 'Connecting...',
  'settings.feishuStatusConnected': 'Connected',
  'settings.feishuStatusError': 'Connection error',
  // Feishu enable
  'settings.feishuEnabled': 'Enabled',
  'settings.feishuDisabled': 'Disabled',
  'settings.feishuEnableSwitch': 'Integration switch',
  // Feishu notify mode
  'settings.feishuNotifyMode': 'Default notification mode',
  'settings.feishuNotifyModeDesc': 'In smart mode, replies are only pushed to Feishu when you are not actively viewing the session.',
  'settings.feishuNotifyAuto': 'Smart',
  'settings.feishuNotifyAlways': 'Always',
  'settings.feishuNotifyOff': 'Off',
  // Feishu config
  'settings.feishuConfigTitle': 'Bot Config',
  'settings.feishuConfigDesc': 'Create a custom app in the Feishu Open Platform and fill in its credentials here',
  'settings.feishuSecretHint': 'Leave blank to keep the currently saved secret',
  'settings.feishuSecretPlaceholder': 'Enter App Secret',
  'settings.feishuCliProfileName': 'Lark CLI profile',
  'settings.feishuCliProfileNameDesc': 'Profile name inside OKClaw’s isolated CLI home. It does not write to the user’s global Lark CLI config.',
  'settings.feishuCliProfileNamePlaceholder': 'okclawpro',
  'settings.feishuFillAppId': 'Please enter App ID',
  'settings.feishuFillCredentials': 'Please enter App ID and App Secret',
  'settings.feishuSaved': 'Feishu config saved',
  'settings.feishuSaveFailed': 'Failed to save Feishu config',
  'settings.feishuTest': 'Test Connection',
  'settings.feishuSave': 'Save Config',
  'settings.feishuTestFailed': 'Connection test failed',
  'settings.feishuRegisterCtaTitle': 'Create Feishu Bot by QR code',
  'settings.feishuRegisterCtaDesc': 'Scan the official Feishu flow to create the app. OKClaw imports App ID / App Secret, saves the config, and starts the Bot.',
  'settings.feishuRegisterButton': 'Create by QR',
  'settings.feishuRegisterTitle': 'Create Feishu Bot by QR code',
  'settings.feishuRegisterDesc': 'OKClaw opens the official Feishu QR creation flow. After scanning, it imports App ID / App Secret, saves OKClaw config, and initializes the isolated lark-cli profile.',
  'settings.feishuRegisterStarting': 'Requesting a Feishu QR code...',
  'settings.feishuRegisterScanPrompt': 'Scan with the Feishu app and follow the page to finish app creation',
  'settings.feishuRegisterWaiting': 'Waiting for scan confirmation...',
  'settings.feishuRegisterOpenLink': 'Or open the link in your browser',
  'settings.feishuRegisterSuccess': 'App created',
  'settings.feishuRegisterSuccessDesc': 'Credentials imported. Saving config and starting the Bot.',
  'settings.feishuRegisterFailed': 'Creation failed',
  'settings.feishuRegisterFailedDesc': 'Try again later, or configure App ID / App Secret manually.',
  'settings.feishuRegisterClose': 'Close',
  'settings.feishuRegisterSaved': 'Feishu Bot created and saved. Starting now.',
  'settings.feishuRegisterNeedsSecret': 'QR registration did not return complete credentials. Try again or configure manually.',
  'settings.feishuBotListTitle': 'Feishu Bot List',
  'settings.feishuBotListDesc': 'Manage Feishu Bot credentials and connection state from one place.',
  'settings.feishuDefaultBotName': 'Feishu Assistant',
  'settings.feishuManualAdd': 'Manual Add',
  'settings.feishuBotUnconfigured': 'Not configured',
  'settings.feishuBotConfigExpandedHint': 'Edit credentials, access control, and runtime preferences for this Bot. Start/stop controls are in the Bot row.',
  'settings.feishuBridgeActiveBindingsInline': '{count} active bindings',
  // Feishu bridge
  'settings.feishuBridgeTitle': 'Bridge Status',
  'settings.feishuBridgeDesc': 'Status of the Feishu long connection and current session bindings',
  'settings.feishuBridgeConnection': 'Connection status',
  'settings.feishuBridgeActiveBindings': 'Active bindings',
  'settings.feishuBridgeActiveBindingsDesc': 'Number of Feishu chats currently mapped',
  'settings.feishuBridgeStopped': 'Feishu Bridge stopped',
  'settings.feishuBridgeEnableFirst': 'Enable Feishu integration first',
  'settings.feishuBridgeStarting': 'Feishu Bridge is starting',
  'settings.feishuBridgeStartFailed': 'Failed to start Feishu Bridge',
  'settings.feishuBridgeCheckConfig': 'Please check the configuration',
  'settings.feishuCliStatus': 'CLI runtime',
  'settings.feishuCliStatusProfile': 'Using profile {profile}',
  'settings.feishuCliStatusDefaultProfile': 'Using OKClaw default profile okclawpro',
  // Feishu access
  'settings.feishuAccessTitle': 'Access Control',
  'settings.feishuAccessDesc': 'Restrict who can run this Feishu bot. Leave lists empty to allow all.',
  'settings.feishuAllowedUsers': 'Allowed users',
  'settings.feishuAllowedUsersDesc': 'Open IDs allowed to send tasks or commands. One per line or comma-separated.',
  'settings.feishuAllowedUsersPlaceholder': 'ou_xxxxxxxx',
  'settings.feishuAllowedChats': 'Allowed chats',
  'settings.feishuAllowedChatsDesc': 'Chat IDs allowed to use the bot. One per line or comma-separated.',
  'settings.feishuAllowedChatsPlaceholder': 'oc_xxxxxxxx',
  'settings.feishuAdmins': 'Admins',
  'settings.feishuAdminsDesc': 'Open IDs that can manage bindings and privileged commands.',
  'settings.feishuAdminsPlaceholder': 'ou_admin_xxxxxxxx',
  'settings.feishuRequireMentionInGroup': 'Require @ mention in groups',
  'settings.feishuRequireMentionInGroupDesc': 'Group messages only trigger the bot when the bot is mentioned.',
  // Feishu runtime
  'settings.feishuRuntimeTitle': 'Runtime Behavior',
  'settings.feishuRuntimeDesc': 'Tune reply behavior and runtime preferences for this Feishu bot.',
  'settings.feishuMessageReply': 'Reply in thread',
  'settings.feishuMessageReplyDesc': 'Send bot responses as replies to the original Feishu message.',
  'settings.feishuRunIdleTimeoutMinutes': 'Run idle timeout',
  'settings.feishuRunIdleTimeoutMinutesDesc': 'Minutes to wait before considering an idle run timed out. Empty uses the runtime default.',
  'settings.feishuRunIdleTimeoutMinutesPlaceholder': '30',
  'settings.feishuShowToolCalls': 'Show tool calls',
  'settings.feishuShowToolCallsDesc': 'Saved as a display preference; currently used only by supported card rendering paths.',
  'settings.feishuMaxConcurrentRuns': 'Max concurrent runs',
  'settings.feishuMaxConcurrentRunsDesc': 'Reserved concurrency preference. A single chat is still processed through its queue. Empty uses the runtime default.',
  'settings.feishuMaxConcurrentRunsPlaceholder': '2',
  'settings.feishuCliStatusToggle': 'Show CLI status summary',
  'settings.feishuCliStatusToggleDesc': 'Saved as a display preference; it does not mean the CLI binary or profile has passed diagnostics.',
  // Feishu bindings
  'settings.feishuBindingsTitle': 'Chat Bindings',
  'settings.feishuBindingsDesc': 'View the mapping between Feishu chats and OKClaw Agent sessions',
  'settings.feishuBindingsEmpty': 'No active bindings yet. After Bridge starts, sending a message to the bot in Feishu will create one automatically.',
  'settings.feishuBindingsGroups': 'Groups ({count})',
  'settings.feishuBindingsDirects': 'Direct chats ({count})',
  'settings.feishuBindingsUnnamed': 'Unnamed group',
  'settings.feishuBindingsDirect': 'Feishu direct chat',
  'settings.feishuBindingsGroupType': 'Group',
  'settings.feishuBindingsDirectType': 'Direct',
  'settings.feishuBindingsTopic': 'Topic',
  'settings.feishuBindingsRemoveConfirm': 'Remove this Feishu binding?',
  'settings.feishuBindingsSession': 'Session',
  'settings.feishuBindingsCurrent': 'Current session',
  'settings.feishuBindingsRefreshFailed': 'Failed to refresh bindings',
  'settings.feishuBindingsUpdated': 'Binding updated',
  'settings.feishuBindingsUpdateFailed': 'Failed to update binding',
  'settings.feishuBindingsRemoved': 'Binding removed',
  'settings.feishuBindingsRemoveFailed': 'Failed to remove binding',
  // Feishu permissions
  'settings.feishuPermissionsTitle': 'Permission Config',
  'settings.feishuPermissionsDesc': 'Paste the JSON below into the Feishu Open Platform permission page to request extended scopes that OKClaw native Feishu capabilities may use. Some sensitive scopes require admin review or extra enablement.',
  'settings.feishuPermissionsCopy': 'Copy permission config',
  'settings.feishuPermissionsCopied': 'Permission config copied',
  'settings.feishuPermissionsCopyFailed': 'Copy failed',
  'settings.feishuPermissionsExpand': 'Expand full config',
  'settings.feishuPermissionsCollapse': 'Collapse full config',
  // Feishu setup guide
  'settings.feishuGuideTitle': 'Setup Guide',
  'settings.feishuGuideDesc': 'QR creation is recommended. Expand manual add only when needed.',
  'settings.feishuGuideRuntimeTitle': 'Built-in lark-cli runtime',
  'settings.feishuGuideRuntimeDesc': 'OKClaw bundles the official lark-cli and stores its profile in an isolated app directory. Users do not need to install or configure a global lark-cli for the Bridge.',
  'settings.feishuGuideQrTitle': 'QR creation flow',
  'settings.feishuGuideQrDesc': 'Recommended. The QR flow creates the app, imports credentials, and applies the required setup automatically.',
  'settings.feishuGuideQrStep1Title': 'Click Create by QR',
  'settings.feishuGuideQrStep1Desc': 'Use the Create by QR button above. OKClaw opens the official Feishu creation flow and shows a QR code.',
  'settings.feishuGuideQrStep2Title': 'Scan and confirm',
  'settings.feishuGuideQrStep2Desc': 'Scan with the Feishu app and follow the page prompts. You do not need to manually create an app or copy App Secret afterward.',
  'settings.feishuGuideManualTitle': 'Manual add flow',
  'settings.feishuGuideManualDesc': 'Expand this only when QR creation is unavailable or when you need to use an existing custom app.',
  'settings.feishuGuideStep1Title': 'Create a custom app',
  'settings.feishuGuideStep1Prefix': 'Open the',
  'settings.feishuGuidePlatform': 'Feishu Open Platform',
  'settings.feishuGuideStep1Suffix': ', create a custom app, and enable the Bot capability.',
  'settings.feishuGuideStep2Title': 'Fill in credentials',
  'settings.feishuGuideStep2Desc': 'Copy App ID and App Secret from Credentials & Basic Info, then save them in the settings above.',
  'settings.feishuGuideStep3Title': 'Configure long connection events and callbacks',
  'settings.feishuGuideStep3Intro': 'In Events & Callbacks, choose long connection for both event subscription and callback configuration.',
  'settings.feishuGuideStep3EventsTitle': 'Event subscription',
  'settings.feishuGuideStep3EventsDesc': 'Add the receive-message event',
  'settings.feishuGuideStep3CallbacksTitle': 'Callback configuration',
  'settings.feishuGuideStep3CallbacksDesc': 'Add the card action callback when the platform console exposes it',
  'settings.feishuGuideStep3Fallback': 'The bundled lark-cli 1.0.40 does not expose card.action.trigger yet; OKClaw automatically falls back to the /stop command when card callbacks are unavailable.',
  'settings.feishuGuideTip': 'After the app is published, add the bot to a Feishu group or direct chat and send it a text message to let OKClaw Agent work from Feishu.',
  // Feishu commands
  'settings.feishuCommandsTitle': 'Command Reference',
  'settings.feishuCommandsDesc': 'You can send these commands directly to the bot in Feishu',
  'settings.feishuCmdHelp': 'Show help and command list',
  'settings.feishuCmdNew': 'Create a new Agent session and rebind the current chat',
  'settings.feishuCmdList': 'Show recent sessions in the current workspace',
  'settings.feishuCmdSwitch': 'Switch to the session with the specified index',
  'settings.feishuCmdWorkspace': 'Show or change the default workspace',
  'settings.feishuCmdStop': 'Stop the current Agent task',
  'settings.feishuCmdNow': 'Show current binding status, workspace, and notification mode',
  'settings.feishuCmdStatus': 'Show current binding status, workspace, and notification mode',
  'settings.feishuCmdConfig': 'Show the Feishu Bridge config summary (admin)',
  'settings.feishuCmdTimeout': 'Show or update the run timeout hint',
  'settings.feishuCmdDoctor': 'Show lark-cli diagnostics (admin)',
  'settings.feishuCmdReconnect': 'Restart the Feishu Bridge (admin)',
  // DingTalk bot
  'settings.dingtalk': 'DingTalk',
  'settings.dingtalkDesc': 'Connect via DingTalk Bot Stream mode',
  'settings.dingtalkEnabled': 'Enabled',
  'settings.dingtalkDisabled': 'Disabled',
  'settings.dingtalkEnabledDesc': 'Enable DingTalk bot integration',
  'settings.dingtalkClientId': 'Client ID',
  'settings.dingtalkClientIdHint': 'Create an app on DingTalk Open Platform to get this',
  'settings.dingtalkSecret': 'Client Secret',
  'settings.dingtalkSecretHint': 'App Client Secret',
  'settings.dingtalkSave': 'Save Config',
  'settings.dingtalkSaved': 'DingTalk config saved',
  'settings.dingtalkSaveFailed': 'Save failed',
  'settings.dingtalkTestConnection': 'Test Connection',
  'settings.dingtalkTestSuccess': 'Connection test successful',
  'settings.dingtalkTestFailed': 'Connection test failed',
  'settings.dingtalkConnect': 'Connect',
  'settings.dingtalkDisconnect': 'Disconnect',
  'settings.dingtalkStatus': 'Status',
  'settings.dingtalkStatusConnected': 'Connected',
  'settings.dingtalkStatusConnecting': 'Connecting...',
  'settings.dingtalkStatusDisconnected': 'Disconnected',
  'settings.dingtalkStatusError': 'Error',
  'settings.dingtalkTabConfig': 'Config',
  'settings.dingtalkTabBindings': 'Bindings',
  'settings.dingtalkBindingsEmpty': 'No conversation bindings yet',
  'settings.dingtalkRemoveBinding': 'Remove',
  'settings.dingtalkDefaultWorkspace': 'Default Workspace',
  'settings.dingtalkDefaultWorkspaceDesc': 'Default workspace for new conversations',
  // QQ Bot
  'settings.qqbot': 'QQ',
  'settings.qqbotDesc': 'Connect via QQ Bot WebSocket',
  'settings.qqbotEnabled': 'Enabled',
  'settings.qqbotDisabled': 'Disabled',
  'settings.qqbotEnabledDesc': 'Enable QQ Bot integration',
  'settings.qqbotAppId': 'AppID',
  'settings.qqbotAppIdHint': 'Create a bot on QQ Open Platform to get this',
  'settings.qqbotSecret': 'AppSecret',
  'settings.qqbotSecretHint': 'Bot AppSecret',
  'settings.qqbotSave': 'Save Config',
  'settings.qqbotSaved': 'QQ Bot config saved',
  'settings.qqbotSaveFailed': 'Save failed',
  'settings.qqbotTestConnection': 'Test Connection',
  'settings.qqbotTestSuccess': 'Connection test successful',
  'settings.qqbotTestFailed': 'Connection test failed',
  'settings.qqbotConnect': 'Connect',
  'settings.qqbotDisconnect': 'Disconnect',
  'settings.qqbotStatus': 'Status',
  'settings.qqbotStatusConnected': 'Connected',
  'settings.qqbotStatusConnecting': 'Connecting...',
  'settings.qqbotStatusDisconnected': 'Disconnected',
  'settings.qqbotStatusError': 'Error',
  'settings.qqbotTabConfig': 'Config',
  'settings.qqbotTabBindings': 'Bindings',
  'settings.qqbotBindingsEmpty': 'No conversation bindings yet',
  'settings.qqbotRemoveBinding': 'Remove',
  'settings.qqbotDefaultWorkspace': 'Default Workspace',
  'settings.qqbotDefaultWorkspaceDesc': 'Default workspace for new conversations',
  // WeCom (Enterprise WeChat bot)
  'settings.wecom': 'WeCom',
  'settings.wecomDesc': 'WeCom (Enterprise WeChat) bot integration',
  'settings.wecomSaved': 'WeCom settings saved',
  'settings.wecomSaveFailed': 'Failed to save WeCom settings',
  'settings.wecomEnabled': 'Enable WeCom',
  'settings.wecomEnabledDesc': 'Connect to WeCom via bot WebSocket',
  'settings.wecomBotId': 'Bot ID',
  'settings.wecomSecret': 'Secret',
  'settings.wecomWsUrl': 'WebSocket URL',
  'settings.wecomSendThinking': 'Send thinking message',
  'settings.wecomSendThinkingDesc': 'Show a thinking placeholder while generating response',
  'settings.wecomScanQr': 'Scan QR Code',
  'settings.wecomScanQrDesc': 'Scan with WeCom app to auto-fill Bot ID and Secret',
  'settings.wecomScanning': 'Please scan the QR code with WeCom app...',
  'settings.wecomScanSuccess': 'QR scan successful, Bot ID and Secret filled',
  'settings.wecomScanTimeout': 'QR scan timed out, please try again',
  'settings.wecomScanFailed': 'Failed to generate QR code',
  'settings.wecomConnect': 'Connect',
  'settings.wecomDisconnect': 'Disconnect',
  'settings.wecomTestConnection': 'Test',
  'settings.wecomStatus': 'Status',
  'settings.wecomStatusDisconnected': 'Disconnected',
  'settings.wecomStatusConnecting': 'Connecting...',
  'settings.wecomStatusConnected': 'Connected',
  'settings.wecomStatusError': 'Error',
  'settings.wecomBindings': 'Conversation Bindings',
  'settings.wecomBindingsDesc': 'Manage contact-to-session mappings',
  'settings.wecomDefaultWorkspace': 'Default Workspace',
  'settings.wecomDefaultWorkspaceDesc': 'Default workspace for new conversations',
  'settings.wecomNoBindings': 'No conversation bindings yet',
  'settings.wecomRemoveBinding': 'Remove',
  'settings.wecomBindingUser': 'Contact',
  'settings.wecomBindingChat': 'Group Chat',
  'settings.wecomBindingSession': 'Session',
  'settings.wecomBindingWorkspace': 'Workspace',
  'settings.wecomAccountConfig': 'Account & Config',
  'settings.wecomNotifyMode': 'Notification Mode',
  'settings.wecomNotifyModeDesc': 'Control message notification behavior',
  // WeChat
  'settings.wechat': 'WeChat',
  'settings.wechatDesc': 'WeChat integration',
  'settings.shortcuts': 'Shortcuts',
  'settings.shortcuts.description': 'Keyboard shortcuts',
  // Settings section titles
  'settings.basicInfo': 'Basic Info',
  'settings.basicInfo.description': 'Help OKClaw Pro personalize responses to you.',
  'settings.location': 'Location',
  'settings.location.description': 'Enables location-aware responses like weather, local time, and regional context.',
  'settings.notes': 'Notes',
  'settings.notes.description': 'Free-form context that helps OKClaw Pro understand your preferences.',
  'settings.name': 'Name',
  'settings.name.description': 'How OKClaw Pro should address you.',
  'settings.name.placeholder': 'Your name',
  'settings.timezone': 'Timezone',
  'settings.timezone.description': 'Used for relative dates like \'tomorrow\' or \'next week\'.',
  'settings.timezone.placeholder': 'e.g., America/New_York',
  'settings.responseLanguage': 'Language',
  'settings.responseLanguage.description': 'Preferred language for OKClaw Pro\'s responses.',
  'settings.responseLanguage.placeholder': 'e.g., English',
  'settings.city': 'City',
  'settings.city.description': 'Your city for local information and context.',
  'settings.city.placeholder': 'e.g., New York',
  'settings.country': 'Country',
  'settings.country.description': 'Your country for regional formatting and context.',
  'settings.country.placeholder': 'e.g., USA',
  'settings.notesPlaceholder': 'Any additional context you\'d like OKClaw Pro to know...',
  'settings.editFile': 'Edit File',
  // App settings
  'settings.notifications': 'Notifications',
  'settings.power': 'Power',
  'settings.network': 'Network',
  'settings.about': 'About',
  'settings.aboutOKClaw': 'About OKClaw',
  'settings.aboutOKClaw.description': 'About OKClaw Pro',
  'aboutOKClaw.officialWebsite': 'Official Website',
  'aboutOKClaw.recommendedAI': 'Recommended AI',
  'aboutOKClaw.contactSupport': 'Contact Support',
  'aboutOKClaw.scanToContact': 'Scan the QR code to contact customer service',
  // Appearance settings
  'settings.defaultTheme': 'Default Theme',
  'settings.workspaceThemes': 'Workspace Themes',
  'settings.interface': 'Interface',
  'settings.toolIcons': 'Tool Icons',
  'settings.tool': 'Tool',
  'settings.appearance.icon': 'Icon',
  'settings.appearance.commands': 'Commands',
  'settings.appearance.themeDefault': 'Default',
  'settings.appearance.mode': 'Mode',
  'settings.appearance.system': 'System',
  'settings.appearance.light': 'Light',
  'settings.appearance.dark': 'Dark',
  'settings.appearance.colorTheme': 'Color theme',
  'settings.appearance.font': 'Font',
  'settings.appearance.fontInter': 'Inter',
  'settings.appearance.fontSystem': 'System',
  'settings.appearance.overrideThemePerWorkspace': 'Override theme settings per workspace',
  'settings.appearance.useDefaultWithLabel': 'Use Default ({label})',
  'settings.appearance.useDefault': 'Use Default',
  'settings.appearance.connectionIcons': 'Connection icons',
  'settings.appearance.connectionIcons.description': 'Show provider icons in the session list and model selector',
  'settings.appearance.richToolDescriptions': 'Rich tool descriptions',
  'settings.appearance.richToolDescriptions.description': 'Add action names and intent descriptions to all tool calls. Provides richer activity context in sessions.',
  'settings.appearance.toolIconsDescription': 'Icons shown next to CLI commands in chat activity. Stored in ~/.okclawpro/tool-icons/.',
  'settings.appearance.editFile': 'Edit File',
  'settings.appearance.searchTools': 'Search tools...',
  'settings.appearance.noToolIconMappings': 'No tool icon mappings found',
  // Input settings
  'settings.typing': 'Typing',
  'settings.typing.description': 'Control how text is entered in the chat input.',
  'settings.sending': 'Sending',
  'settings.sending.description': 'Choose how to send messages.',
  'settings.input.autoCapitalisation': 'Auto capitalisation',
  'settings.input.autoCapitalisation.description': 'Automatically capitalise the first letter when typing a message.',
  'settings.input.spellCheck': 'Spell check',
  'settings.input.spellCheck.description': 'Underline misspelled words while typing.',
  'settings.input.sendMessageWith': 'Send message with',
  'settings.input.sendMessageWith.description': 'Keyboard shortcut for sending messages',
  'settings.input.enter': 'Enter',
  'settings.input.enter.description': 'Use Shift+Enter for new lines',
  'settings.input.cmdEnter': '\u2318 Enter',
  'settings.input.ctrlEnter': 'Ctrl+Enter',
  'settings.input.cmdEnter.description': 'Use Enter for new lines',
  // Labels settings
  'settings.aboutLabels': 'About Labels',
  'settings.aboutLabels.description1': 'Labels help you organize sessions with colored tags. Use them to categorize conversations by project, topic, or priority — making it easy to filter and find related sessions later.',
  'settings.aboutLabels.description2.prefix': 'Each label can optionally carry a ',
  'settings.aboutLabels.description2.value': 'value',
  'settings.aboutLabels.description2.suffix': ' with a specific type (text, number, or date). This turns labels into structured metadata — for example, a "priority" label with value 3, or a "due" label with a date.',
  'settings.aboutLabels.description3.prefix': '',
  'settings.aboutLabels.description3.autoApply': 'Auto-apply rules',
  'settings.aboutLabels.description3.suffix': ' assign labels automatically when a message matches a regex pattern. For example, pasting a Linear issue URL can auto-tag the session with the project name and issue ID — no manual tagging needed.',
  'settings.aboutLabels.learnMore': 'Learn more',
  'settings.labelHierarchy': 'Label Hierarchy',
  'settings.labelHierarchy.description': 'All labels configured for this workspace. Labels can be nested to form groups.',
  'settings.labelHierarchy.fullscreenTitle': 'Label Hierarchy',
  'settings.labelHierarchy.empty': 'No labels configured.',
  'settings.labelHierarchy.emptyHint': 'Labels can be created by the agent or by editing {file} in your workspace.',
  'settings.autoApplyRules': 'Auto-Apply Rules',
  'settings.autoApplyRules.description': 'Regex patterns that automatically apply labels when matched in user messages. For example, paste a Linear issue URL and automatically tag the session with the project name and issue ID.',
  'settings.autoApplyRules.fullscreenTitle': 'Auto-Apply Rules',
  // Permissions settings
  'settings.aboutPermissions': 'About Permissions',
  'settings.aboutPermissions.description1.prefix': 'Permissions control how much autonomy your agent has. In ',
  'settings.aboutPermissions.description1.explore': 'Explore',
  'settings.aboutPermissions.description1.middle': ' mode, the agent can only read and research — perfect for understanding a problem before committing to changes. When you\'re ready, switch to ',
  'settings.aboutPermissions.description1.execute': 'Execute',
  'settings.aboutPermissions.description1.suffix': ' mode to let the agent implement the plan autonomously.',
  'settings.aboutPermissions.description2': 'A good workflow: start in Explore to let the agent investigate, review the proposed plan, then execute with confidence.',
  'settings.aboutPermissions.learnMore': 'Learn more',
  'settings.defaultPermissions': 'Default Permissions',
  'settings.defaultPermissions.description': 'App-level patterns allowed in Explore mode. Commands not on this list are blocked.',
  'settings.defaultPermissions.fullscreenTitle': 'Default Permissions',
  'settings.defaultPermissions.empty': 'No default permissions found.',
  'settings.defaultPermissions.emptyHint': 'Default permissions should be at {file}',
  'settings.workspaceCustomizations': 'Workspace Customizations',
  'settings.workspaceCustomizations.description': 'Workspace-level patterns that extend the app defaults above.',
  'settings.workspaceCustomizations.fullscreenTitle': 'Workspace Customizations',
  'settings.workspaceCustomizations.empty': 'No custom permissions configured.',
  'settings.workspaceCustomizations.emptyHint': 'Create a {file} file in your workspace to add custom rules.',
  'settings.permissions.customBlockedTool': 'Custom blocked tool',
  'settings.permissions.customBashPattern': 'Custom bash pattern',
  'settings.permissions.customMcpPattern': 'Custom MCP pattern',
  'settings.permissions.customApiEndpoint': 'Custom API endpoint',
  'settings.permissions.allowedWritePath': 'Allowed write path',
  // Server settings
  'settings.remoteAccess': 'Remote Access',
  'settings.connection': 'Connection',
  'settings.portPlaceholder': '9100',
  'serverSettings.enableServerMode': 'Enable Server Mode',
  'serverSettings.enableServerModeDesc': 'Allow connections from other machines on the network.',
  'serverSettings.restartRequired': 'Restart required to apply changes',
  'serverSettings.restartNow': 'Restart Now',
  'serverSettings.enabling': 'Enabling...',
  'serverSettings.disabling': 'Disabling...',
  'serverSettings.publishService': 'Remote Access Service',
  'serverSettings.publishServiceTab': 'Remote Access Service',
  'serverSettings.myDevicesTab': 'My Devices',
  'serverSettings.publishServiceDesc': 'After enabling, OKClaw will start a remote access service so you can access and use OKClaw from another device in a browser. Make sure the current workspace does not contain sensitive information.',
  'serverSettings.servicePortLabel': 'Server Port',
  'serverSettings.servicePortDesc': 'Start the remote access service on the specified port. Set to 0 to use a random port.',
  'serverSettings.serviceAccessUrlLabel': 'Service Access URL',
  'serverSettings.serviceAccessUrlDesc': 'Network addresses that may reach the remote access service',
  'serverSettings.serviceAccessUrlAfterRestart': 'Available after restart',
  'serverSettings.serviceAccessUrlUnavailable': 'Unavailable while the service is starting',
  'serverSettings.accessTokenLabel': 'Access Token',
  'serverSettings.accessTokenDesc': 'Enter this token in the browser to verify access',
  'serverSettings.resetAccessToken': 'Reset token',
  'serverSettings.resettingAccessToken': 'Resetting',
  'serverSettings.accessTokenReset': 'Access token reset',
  'serverSettings.publicAccessLabel': 'Public Access',
  'serverSettings.publicAccessDesc': 'Generate a public browser address. After the connection is established, chats and files prefer peer-to-peer transfer.',
  'serverSettings.publicAccessUrlLabel': 'Public Access URL',
  'serverSettings.publicAccessUrlDesc': 'Open this address from an external browser to connect to this desktop app',
  'serverSettings.publicAccessUrlWarning': 'Note: Keep this link private. Anyone who can open it may connect to this computer and access local chats and related workspace content after signing in.',
  'serverSettings.publicAccessUrlUnavailable': 'Available after the public connection is established',
  'serverSettings.publicAccessUrlError': 'Public access is not ready: {error}',
  'serverSettings.publicAccessDevicesLabel': 'My Devices',
  'serverSettings.publicAccessDevicesDesc': 'Desktop devices under the current OKClaw account. Offline devices are kept here, and this computer is marked automatically.',
  'serverSettings.publicAccessDevicesEmpty': 'No devices yet',
  'serverSettings.publicAccessDeviceCurrent': 'This computer',
  'serverSettings.publicAccessDeviceOnline': 'Online',
  'serverSettings.publicAccessDeviceOffline': 'Offline',
  'serverSettings.publicAccessDeviceConnectionCount': '{count} remote connections',
  'serverSettings.publicAccessDeviceConnectionsLabel': 'Connection Details',
  'serverSettings.publicAccessDeviceDelete': 'Delete',
  'serverSettings.publicAccessDeviceDeleting': 'Deleting',
  'serverSettings.publicAccessDeviceDeleted': 'Device deleted',
  'serverSettings.publicAccessDeviceDeleteConfirm': 'Delete {name} from My Devices? If it is online, its public access connection will be disabled.',
  'serverSettings.publicAccessDeviceDeleteFailed': 'Could not delete device: {error}',
  'serverSettings.publicAccessConnectionsLabel': 'Active Remote Connections',
  'serverSettings.publicAccessConnectionsDesc': 'Browsers currently connected to this desktop app through public access',
  'serverSettings.publicAccessConnectionsEmpty': 'No connected browsers',
  'serverSettings.publicAccessConnectionFallback': 'Remote browser',
  'serverSettings.publicAccessConnectionConnectedFor': 'Connected for {duration}',
  'serverSettings.publicAccessConnectionLessThanMinute': 'less than 1 minute',
  'serverSettings.publicAccessConnectionMinutes': '{count} minutes',
  'serverSettings.publicAccessConnectionHours': '{count} hours',
  'serverSettings.publicAccessConnectionDisconnect': 'Disconnect',
  'serverSettings.publicAccessConnectionDisconnecting': 'Disconnecting',
  'serverSettings.publicAccessConnectionDisconnected': 'Remote connection disconnected',
  'serverSettings.publicAccessConnectionDisconnectFailed': 'Could not disconnect remote connection: {error}',
  'serverSettings.portLabel': 'Port',
  'serverSettings.webUrlLabel': 'Browser Login URL',
  'serverSettings.urlLabel': 'URL',
  'serverSettings.tokenLabel': 'Token',
  'serverSettings.certificateLabel': 'Certificate',
  'serverSettings.privateKeyLabel': 'Private Key',
  'serverSettings.notConfigured': 'Not configured',
  'serverSettings.browse': 'Browse',
  'serverSettings.reset': 'Reset',
  'serverSettings.save': 'Save',
  'serverSettings.saved': 'Remote access settings saved',
  'serverSettings.copiedToClipboard': '{label} copied to clipboard',
  'serverSettings.portError': 'Port must be 0 or between 1024 and 65535',
  'serverSettings.privateKeyRequired': 'Private key is required when a certificate is provided',
  'serverSettings.certificateRequired': 'Certificate is required when a private key is provided',
  'serverSettings.insecureWarningRunning': 'Server is running without TLS. Auth tokens are sent in cleartext.',
  'serverSettings.insecureWarningGeneral': 'Without TLS, connections will be unencrypted.',
  'serverSettings.webUnavailableTitle': 'Manage remote access on the desktop app',
  'serverSettings.webUnavailableDesc': 'This browser is accessing OKClaw through the desktop app. Use the desktop settings to enable or disable public access and reset the access token.',
  'webui.accessToken.eyebrow': 'Browser Access',
  'webui.accessToken.title': 'Enter Access Token',
  'webui.accessToken.description': 'Use the access token shown in the desktop Remote Access settings to connect to this OKClaw desktop app.',
  'webui.accessToken.inputLabel': 'Access token',
  'webui.accessToken.placeholder': 'Paste access token',
  'webui.accessToken.submit': 'Enter',
  'webui.accessToken.connecting': 'Connecting...',
  'webui.accessToken.required': 'Access token is required.',
  'webui.accessToken.invalid': 'Access token is incorrect or expired.',
  'webui.accessToken.configFailed': 'Failed to fetch config: {status}',
  'webui.accessToken.noWsUrl': 'Server did not return a WebSocket URL',
  'webui.accessToken.desktopSignedOut': 'The desktop app is not signed in. Sign in on the desktop app first.',
  'webui.accessToken.p2pUnsupported': 'This browser does not support public peer-to-peer access.',
  'webui.accessToken.publicDesktopOffline': 'The desktop app is not connected to the public access service. Check that Remote Access and Public Access are enabled on the desktop app.',
  'webui.accessToken.publicSignalingFailed': 'Could not connect to the public access service. Try again later or check the desktop network.',
  'webui.accessToken.publicP2pTimeout': 'The public peer-to-peer connection timed out. Make sure both devices can access the internet and the network allows UDP/WebRTC connections.',
  'webui.accessToken.publicP2pFailed': 'The public peer-to-peer connection failed. Check both networks and try again.',
  'webui.accessToken.connectionTimeout': 'Connection timed out. Make sure Remote Access is enabled on the desktop app and the desktop app is online, then try again.',
  'webui.accessToken.connectionFailed': 'Connection failed. Make sure Remote Access is enabled on the desktop app and the desktop app is online, then try again.',
  'webui.accessToken.disconnectedByDesktop': 'The desktop app disconnected this browser. Enter the access token again to continue.',
  'webui.accessToken.securityNote': 'Note: Only enter this token on devices you trust. Anyone with a valid token can access this desktop app through the browser.',
  'webui.loading.connecting': 'Connecting to server...',
  'webui.remote.title': 'OKClaw Remote',
  'webui.remote.checkingAuth': 'Checking account session...',
  'webui.remote.refresh': 'Refresh',
  'webui.remote.logout': 'Sign out',
  'webui.remote.retry': 'Retry',
  'webui.remote.backToList': 'Back to devices',
  'webui.remote.rename': 'Rename',
  'webui.remote.online': 'Online',
  'webui.remote.offline': 'Offline',
  'webui.remote.connect': 'Connect',
  'webui.remote.connectionCount': '{count} active connection(s)',
  'webui.remote.lastSeenAt': 'Last seen: {time}',
  'webui.remote.disconnectedByDesktop': 'Offline',
  'webui.remote.login.title': 'Sign in to OKClaw',
  'webui.remote.login.description': 'Use your OKClaw account to choose one of your online desktop devices.',
  'webui.remote.login.passwordTab': 'Password',
  'webui.remote.login.smsTab': 'SMS code',
  'webui.remote.login.account': 'Account',
  'webui.remote.login.accountPlaceholder': 'Email, phone, or account',
  'webui.remote.login.password': 'Password',
  'webui.remote.login.passwordPlaceholder': 'Enter password',
  'webui.remote.login.phone': 'Phone',
  'webui.remote.login.phonePlaceholder': 'Enter phone number',
  'webui.remote.login.smsCode': 'SMS code',
  'webui.remote.login.smsCodePlaceholder': 'Enter code',
  'webui.remote.login.submit': 'Sign in',
  'webui.remote.login.signingIn': 'Signing in...',
  'webui.remote.devices.title': 'My devices',
  'webui.remote.devices.signedIn': 'Signed in',
  'webui.remote.empty.title': 'No devices yet',
  'webui.remote.empty.description': 'Open OKClaw on a desktop device and enable remote access, then refresh this list.',
  'webui.remote.device.lastSeenNever': 'Never',
  'webui.remote.device.lastSeenUnknown': 'Unknown',
  'webui.remote.device.renameFailed': 'Could not rename this device. Please try again.',
  'webui.remote.device.delete': 'Delete device',
  'webui.remote.device.deleteConfirm': 'Delete {name} from My devices? If it is online, its public access connection will be disabled.',
  'webui.remote.device.deleteFailed': 'Could not delete this device. Please try again.',
  'webui.remote.connecting.title': 'Connecting to device',
  'webui.remote.connecting.description': 'Preparing a secure connection to {name}.',
  'webui.remote.connecting.ticketReady': 'Connection ticket received. Establishing a secure connection.',
  'webui.remote.connecting.failedTitle': 'Connection failed',
  'webui.remote.error.title': 'Remote access unavailable',
  'webui.remote.error.unknown': 'Something went wrong. Please try again.',
  'webui.remote.error.checkAuthFailed': 'Could not check your account session. Please try again.',
  'webui.remote.error.loadDevicesFailed': 'Could not load your devices. Please try again.',
  'webui.remote.error.connectFailed': 'Could not create a connection ticket. Please try again.',
  'webui.remote.error.loginFailed': 'Sign in failed. Please check your information and try again.',
  'webui.remote.error.AUTH_NOT_CONFIGURED': 'Account sign-in is not configured on this gateway yet.',
  'webui.remote.error.AUTH_UNAVAILABLE': 'Account sign-in is temporarily unavailable. Please try again later.',
  'webui.remote.error.INVALID_CREDENTIALS': 'The account or password is incorrect.',
  'webui.remote.error.SMS_CODE_INVALID': 'The SMS code is incorrect.',
  'webui.remote.error.SMS_CODE_EXPIRED': 'The SMS code has expired. Please request a new one.',
  'syncSettings.connectionTitle': 'WebDAV Connection',
  'syncSettings.connectionDescription': 'Configure the WebDAV location used for sync, backup, and restore.',
  'syncSettings.storageNoticeTitle': 'Sync Storage Notice',
  'syncSettings.storageNoticeSecurity': 'To protect data security, OKClaw does not store any user conversation data.',
  'syncSettings.storageNoticeStorage': 'If you need sync capability, you can use this feature and keep all data in the WebDAV drive you provide.',
  'syncSettings.storageNoticeProvidersPrefix': 'You can use a NAS with WebDAV support or a cloud drive provider such as ',
  'syncSettings.storageNoticeProvidersSuffix': '.',
  'syncSettings.recommendedProviders': 'Recommended WebDAV providers:',
  'syncSettings.recommendedProviderTianyi': 'Tianyi Research Cloud Drive',
  'syncSettings.recommendedProviderJianguoyun': 'Jianguoyun',
  'syncSettings.baseUrl': 'Server URL',
  'syncSettings.baseUrlDescription': 'Example: https://dav.example.com/remote.php/dav/files/demo',
  'syncSettings.username': 'Username',
  'syncSettings.usernameDescription': 'Account used to connect to WebDAV.',
  'syncSettings.usernamePlaceholder': 'Enter username',
  'syncSettings.rootPath': 'Remote Path',
  'syncSettings.rootPathDescription': 'The app keeps its latest snapshot under this directory.',
  'syncSettings.password': 'Password',
  'syncSettings.passwordDescription': 'Enter the WebDAV password.',
  'syncSettings.passwordSavedHint': 'A password is already saved. Leave this blank and save to keep it, or clear and save to remove it.',
  'syncSettings.passwordPlaceholder': 'Enter WebDAV password',
  'syncSettings.passwordRequired': 'Please enter or keep a WebDAV password.',
  'syncSettings.testConnection': 'Test Connection',
  'syncSettings.testingConnection': 'Testing...',
  'syncSettings.testSuccess': 'WebDAV connection test succeeded.',
  'syncSettings.testFailed': 'WebDAV connection test failed.',
  'syncSettings.saving': 'Saving...',
  'syncSettings.saved': 'Sync settings saved.',
  'syncSettings.unsavedChanges': 'You have unsaved changes.',
  'syncSettings.saveChanges': 'Save Changes',
  'syncSettings.revertChanges': 'Revert Changes',
  'syncSettings.modeTitle': 'Sync Mode',
  'syncSettings.modeDescription': 'Choose between manual sync and scheduled automatic sync.',
  'syncSettings.modeManual': 'Manual',
  'syncSettings.modeAuto': 'Automatic',
  'syncSettings.interval': 'Auto Sync Interval',
  'syncSettings.intervalDescription': 'Automatic sync runs on a fixed hourly cadence.',
  'syncSettings.everyHour': 'Every 1 hour',
  'syncSettings.every3Hours': 'Every 3 hours',
  'syncSettings.every5Hours': 'Every 5 hours',
  'syncSettings.contentTitle': 'Sync Content',
  'syncSettings.contentDescription': 'Pick chats and global Skills independently.',
  'syncSettings.chats': 'Chat History',
  'syncSettings.chatsDescription': 'Sync chat sessions and messages from workspaces.',
  'syncSettings.skills': 'Skills',
  'syncSettings.skillsDescription': 'Only sync global custom Skills. Workspace scope does not apply.',
  'syncSettings.chatScope': 'Chat Scope',
  'syncSettings.chatScopeDescription': 'Chats can sync all workspaces or only selected ones.',
  'syncSettings.chatScopeAll': 'All Workspaces',
  'syncSettings.chatScopeSelected': 'Selected Workspaces',
  'syncSettings.selectWorkspaces': 'Choose Workspaces',
  'syncSettings.noWorkspaces': 'There are no workspaces available to sync.',
  'syncSettings.statusTitle': 'Backup & Restore',
  'syncSettings.statusDescription': 'Review the latest snapshot and run manual backup or restore.',
  'syncSettings.activity': 'Current Activity',
  'syncSettings.stateIdle': 'Idle',
  'syncSettings.stateBackupRunning': 'Backing up',
  'syncSettings.stateRestoreRunning': 'Restoring',
  'syncSettings.latestSnapshot': 'Remote latest',
  'syncSettings.snapshotSummary': '{date} · {workspaces} workspaces · {sessions} sessions · {skills} Skills',
  'syncSettings.noSnapshot': 'There is no remote latest snapshot yet.',
  'syncSettings.lastBackup': 'Last Backup',
  'syncSettings.lastRestore': 'Last Restore',
  'syncSettings.lastError': 'Last Error',
  'syncSettings.backupNow': 'Back Up Now',
  'syncSettings.backingUp': 'Backing up...',
  'syncSettings.backupSuccess': 'Backup complete. latest has been updated.',
  'syncSettings.backupSkipped': 'Another sync task is already running, so this auto backup was skipped.',
  'syncSettings.restoreNow': 'Open Restore',
  'syncSettings.onlyLatestHint': 'Only the newest snapshot is kept remotely. Each successful backup replaces latest.',
  'syncSettings.contentRequired': 'Select at least one type of content to sync.',
  'syncSettings.connectionIncomplete': 'Please complete the WebDAV connection details first.',
  'syncSettings.syncInProgressBackup': 'A backup is already running. Wait until it finishes before starting another action.',
  'syncSettings.syncInProgressRestore': 'A restore is already running. Wait until it finishes before starting another action.',
  'syncRestoreDialog.title': 'Restore from WebDAV',
  'syncRestoreDialog.description': 'Choose what to restore from the remote latest snapshot.',
  'syncRestoreDialog.loading': 'Loading the remote latest snapshot...',
  'syncRestoreDialog.loadFailed': 'Failed to load the remote snapshot',
  'syncRestoreDialog.retry': 'Retry',
  'syncRestoreDialog.noSnapshot': 'No latest snapshot is available for restore.',
  'syncRestoreDialog.snapshotCreatedAt': 'Snapshot Overview',
  'syncRestoreDialog.contentTitle': 'Restore Content',
  'syncRestoreDialog.chats': 'Chat History',
  'syncRestoreDialog.skills': 'Skills',
  'syncRestoreDialog.chatScope': 'Chat Workspace Scope',
  'syncRestoreDialog.chatScopeAll': 'All Workspaces',
  'syncRestoreDialog.chatScopeSelected': 'Only selected workspaces',
  'syncRestoreDialog.remoteWorkspaces': 'Remote Workspaces',
  'syncRestoreDialog.noRemoteWorkspaces': 'This snapshot does not contain chat workspaces.',
  'syncRestoreDialog.chatCountUnit': 'sessions',
  'syncRestoreDialog.workspaceConflictPolicy': 'Workspace Conflict Policy',
  'syncRestoreDialog.skillConflictPolicy': 'Skill Conflict Policy',
  'syncRestoreDialog.createCopy': 'Create Copy',
  'syncRestoreDialog.overwrite': 'Overwrite',
  'syncRestoreDialog.workspaceConflicts': 'Workspace conflicts detected',
  'syncRestoreDialog.skillConflicts': 'Skill conflicts detected',
  'syncRestoreDialog.noConflicts': 'No conflicts were detected for the current selection.',
  'syncRestoreDialog.emptySelection': 'Nothing is selected to restore. Adjust the content or workspace scope first.',
  'syncRestoreDialog.restore': 'Restore Now',
  'syncRestoreDialog.restoring': 'Restoring...',
  'syncRestoreDialog.restoreSuccess': 'Restore complete: {workspaces} workspaces and {skills} Skills restored.',
  // Workspace settings
  'settings.workspaceSettings': 'Workspace Settings',
  'settings.workspaceInfo': 'Workspace Info',
  'settings.renameWorkspace': 'Rename workspace',
  'settings.enterWorkspaceName': 'Enter workspace name...',
  'settings.modeCycling': 'Mode Cycling',
  'settings.modeCycling.description': 'Select which modes to cycle through with Shift+Tab',
  'settings.defaultSources': 'Default Sources',
  'settings.defaultSources.description': 'Sources auto-enabled for new sessions',
  'settings.noSourcesConfigured': 'No sources configured in this workspace.',
  'settings.advanced': 'Advanced',
  'settings.workspaceName': 'Name',
  'settings.workspaceIcon': 'Icon',
  'settings.edit': 'Edit',
  'settings.change': 'Change',
  'settings.uploading': 'Uploading...',
  'settings.defaultMode': 'Default mode',
  'settings.defaultMode.description': 'Control what AI can do',
  'settings.mode.safe.dropdownDescription': 'Read-only, no changes allowed',
  'settings.mode.ask.dropdownDescription': 'Prompts before making edits',
  'settings.mode.allow-all.dropdownDescription': 'Full autonomous execution',
  'settings.defaultWorkingDirectory': 'Default Working Directory',
  'settings.defaultWorkingDirectory.notSet': 'Not set (uses session folder)',
  'settings.clear': 'Clear',
  'settings.changeDirectory': 'Change...',
  'settings.localMcpServers': 'Local MCP Servers',
  'settings.localMcpServers.description': 'Enable stdio subprocess servers',
  'settings.atLeastTwoModes': 'At least 2 modes required',
  'settings.noWorkspaceSelected': 'No workspace selected',
  'settings.untitled': 'Untitled',
  // AI settings
  'settings.default': 'Default',
  'settings.default.description': 'Settings for new chats when no workspace override is set.',
  'settings.workspaceOverrides': 'Workspace Overrides',
  'settings.workspaceOverrides.description': 'Override default settings per workspace.',
  'settings.aiConnections': 'Connections',
  'settings.aiConnections.description': 'Manage OKClaw managed connections and multiple third-party endpoint channels.',
  'settings.performance': 'Performance',
  'settings.performance.description': 'Cost and caching options.',
  'settings.renameConnection': 'Rename Connection',
  'settings.enterConnectionName': 'Enter connection name...',

  // AI Settings Page – credential health
  'settings.ai.credentialCorrupted': 'Credential file is corrupted. Please re-authenticate.',
  'settings.ai.credentialOtherMachine': 'Credentials from another machine detected. Please re-authenticate on this device.',
  'settings.ai.credentialNoDefault': 'No credentials found for your default connection.',
  'settings.ai.credentialIssueGeneric': 'Credential issue detected.',
  'settings.ai.credentialIssueTitle': 'Credential Issue Detected',
  'settings.ai.reauthenticate': 'Re-authenticate',

  // AI Settings Page – validation states
  'settings.ai.validating': 'Validating...',
  'settings.ai.connectionValid': 'Connection valid',
  'settings.ai.validationFailed': 'Validation failed',

  // AI Settings Page – connection provider types
  'settings.ai.provider.anthropicSubscription': 'Anthropic Subscription',
  'settings.ai.provider.anthropicApi': 'Anthropic API',
  'settings.ai.provider.anthropicCompatible': 'Anthropic Compatible',
  'settings.ai.provider.awsBedrock': 'AWS Bedrock',
  'settings.ai.provider.googleVertex': 'Google Vertex',
  'settings.ai.provider.craftAgentsBackend': 'OKClaw Pro Backend',
  'settings.ai.provider.craftAgentsBackendCompatible': 'OKClaw Pro Backend Compatible',
  'settings.ai.provider.unknown': 'Unknown',

  // AI Settings Page – connection menu items
  'settings.ai.menu.rename': 'Rename',
  'settings.ai.menu.setAsDefault': 'Set as default',
  'settings.ai.menu.reauthenticate': 'Re-authenticate',
  'settings.ai.menu.edit': 'Edit',
  'settings.ai.menu.validateConnection': 'Validate Connection',
  'settings.ai.menu.delete': 'Delete',

  // AI Settings Page – status labels
  'settings.ai.notAuthenticated': 'Not authenticated',
  'settings.ai.defaultBadge': 'Default',
  'settings.ai.usingDefaults': 'Using defaults',
  'settings.ai.loading': 'Loading...',

  // AI Settings Page – settings labels
  'settings.ai.connectionLabel': 'Connection',
  'settings.ai.connectionDescription': 'API connection for new chats',
  'settings.ai.modelLabel': 'Model',
  'settings.ai.modelDescription': 'AI model for new chats',
  'settings.ai.thinkingLabel': 'Thinking',
  'settings.ai.thinkingDescription': 'Reasoning depth for new chats',

  // AI Settings Page – options
  'settings.ai.useDefault': 'Use default',
  'settings.ai.inheritFromAppSettings': 'Inherit from app settings',
  'settings.ai.showOtherWorkspaces': 'Show {count} other workspaces',
  'settings.ai.hideOtherWorkspaces': 'Hide other workspaces',

  // AI Settings Page – empty state & add button
  'settings.ai.noConnectionSlug': 'No AI connection configured. Please click "Buy Plan" at the bottom-left to get started.',
  'settings.ai.connectionNotFound': 'Connection not found: {slug}. It may have been removed.',
  'settings.ai.noConnectionsConfigured': 'No connections configured. Add a connection to get started.',
  'settings.ai.addConnection': '+ Add Connection',
  'settings.ai.managedConnections': 'OKClaw Managed',
  'settings.ai.thirdPartyConnections': 'Third-party Endpoint Channels',
  'settings.ai.noThirdPartyConnections': 'No third-party endpoint channels configured. Add multiple channels by provider or use case.',
  'settings.ai.buyOfficialPlan': 'Buy Official Plan',
  'settings.ai.addThirdPartyConnection': 'Add Third-party Endpoint Channel',
  'settings.ai.refreshModels': 'Refresh Models',
  'settings.ai.refreshingModels': 'Refreshing...',
  'settings.ai.refreshModelsSuccess': 'Model list updated',
  'settings.ai.refreshModelsFailed': 'Failed to refresh models, please try again later',

  // AI Settings Page – built-in tools
  'settings.ai.builtinTools': 'Built-in Tools',
  'settings.ai.builtinToolsDescription': 'AI-powered tools available in chat sessions.',
  'settings.ai.okclawBuiltinTools': 'OKClaw Tool Configuration',
  'settings.ai.okclawBuiltinToolsDescription': 'Choose the image tool channel, then select separate models for image understanding, image generation, and image-to-SVG conversion.',
  'settings.ai.imageGenerationProvider': 'Tool configuration provider',
  'settings.ai.imageGenerationProviderDescription': 'Choose the tool channel: OKClaw official or one of the third-party endpoint channels you added.',
  'settings.ai.gptImage2Description': 'OKClaw official image generation and reference editing channel.',
  'settings.ai.nanoBananaDescription': 'OKClaw official Gemini image generation and iterative editing channel.',
  'settings.ai.imageUnderstanding': 'Image Understanding',
  'settings.ai.imageUnderstandingDescription': 'AI image recognition, OCR, and content analysis.',
  'settings.ai.builtinTools.imageUnderstandingRoute': 'Image understanding route',
  'settings.ai.builtinTools.imageUnderstandingRouteDescription': 'Choose the model used by the selected tool channel for image understanding, or disable it.',
  'settings.ai.builtinTools.imageGenerationRoute': 'Image generation route',
  'settings.ai.builtinTools.imageGenerationRouteDescription': 'Choose the model used by the selected tool channel for image generation, or disable it.',
  'settings.ai.builtinTools.imageToSvgRoute': 'Image to SVG route',
  'settings.ai.builtinTools.imageToSvgRouteDescription': 'Choose the model used by the selected tool channel for PNG/JPG to SVG conversion, or disable it.',
  'settings.ai.builtinTools.selectChannel': 'Select channel',
  'settings.ai.builtinTools.selectModel': 'Select model',
  'settings.ai.builtinTools.okclawDefaultModel': 'OKClaw default',
  'settings.ai.builtinTools.okclawDefaultModelDescription': 'Use OKClaw managed default image understanding model.',
  'settings.ai.builtinTools.okclawImageToSvgModelDescription': 'Use the OKClaw official AI image-to-SVG model.',
  'settings.ai.builtinTools.unsupportedByChannel': 'Unsupported by this channel',
  'settings.ai.builtinTools.unsupportedByChannelDescription': 'Choose another tool channel or disable this tool.',
  'settings.ai.builtinTools.noImageUnderstandingChannels': 'No third-party channels support image understanding yet.',
  'settings.ai.builtinTools.noImageGenerationChannels': 'No OpenAI-compatible third-party channels support image generation yet.',
  'settings.ai.builtinTools.noOpenAiCompatibleChannels': 'No OpenAI-compatible third-party channels support this tool yet.',
  'settings.ai.builtinTools.saveFailed': 'Failed to save built-in tool configuration',
  'settings.ai.builtinTools.restartRequired': 'This window has not loaded the new tool configuration API yet. Restart the app and try again.',
  'settings.ai.toolRoute.okclaw': 'OKClaw official',
  'settings.ai.toolRoute.okclawDescription': 'Use the OKClaw official tool channel.',
  'settings.ai.toolRoute.connection': 'Third-party channel',
  'settings.ai.toolRoute.connectionDescription': 'Send this tool request to the selected third-party endpoint channel.',
  'settings.ai.toolRoute.disabled': 'Disabled',
  'settings.ai.toolRoute.disabledDescription': 'Do not register this tool in chat sessions.',
  'settings.ai.thirdParty.addTitle': 'Add third-party endpoint channel',
  'settings.ai.thirdParty.editTitle': 'Edit third-party endpoint channel',
  'settings.ai.thirdParty.description': 'Configure an OpenAI-compatible or Anthropic-compatible API endpoint.',
  'settings.ai.thirdParty.dataNotice': 'Conversations, files, images, and tool results may be sent to this Base URL when this connection is used.',
  'settings.ai.thirdParty.name': 'Name',
  'settings.ai.thirdParty.protocol': 'Interface type',
  'settings.ai.thirdParty.baseUrl': 'Base URL',
  'settings.ai.thirdParty.openaiBaseUrlHint': 'Saved as entered; /v1 is not appended automatically.',
  'settings.ai.thirdParty.anthropicBaseUrlHint': 'Saved as entered; requests call the Anthropic-compatible messages endpoint.',
  'settings.ai.thirdParty.apiKeyCreateHint': 'Required. Keys are encrypted and never shown again.',
  'settings.ai.thirdParty.apiKeyEditHint': 'Leave blank to keep the existing key. Enter a new key to replace it.',
  'settings.ai.thirdParty.apiKeyPlaceholderEdit': 'Unchanged',
  'settings.ai.thirdParty.models': 'Models',
  'settings.ai.thirdParty.modelsDescription': 'One model ID per line.',
  'settings.ai.thirdParty.defaultModel': 'Default model',
  'settings.ai.thirdParty.defaultModelDescription': 'Must be one of the configured model IDs.',
  'settings.ai.thirdParty.imageUnderstanding': 'Image understanding',
  'settings.ai.thirdParty.imageGeneration': 'Image generation',
  'settings.ai.thirdParty.setAsDefault': 'Set as default connection',
  'settings.ai.thirdParty.invalidForm': 'Please complete the connection form.',
  'settings.ai.thirdParty.testRequiresKey': 'Testing a draft connection requires an API Key.',
  'settings.ai.thirdParty.errorNameRequired': 'Connection name is required.',
  'settings.ai.thirdParty.errorBaseUrlRequired': 'Base URL is required.',
  'settings.ai.thirdParty.errorModelsRequired': 'At least one model ID is required.',
  'settings.ai.thirdParty.errorDefaultModelInvalid': 'Default model must be in the model list.',
  'settings.ai.thirdParty.errorApiKeyRequired': 'API Key is required.',
  'settings.ai.thirdParty.created': 'Third-party endpoint channel added',
  'settings.ai.thirdParty.createdDescription': 'Select this channel for image understanding, image generation, or image-to-SVG conversion in OKClaw Tool Configuration.',
  'settings.ai.thirdParty.updated': 'Third-party endpoint channel updated',
  'settings.ai.thirdParty.deleted': 'Third-party endpoint channel deleted',
  'settings.ai.thirdParty.deleteConfirm': 'Delete "{name}"? Its saved API key will also be removed.',
  'settings.ai.thirdParty.deleteFailed': 'Failed to delete third-party endpoint channel',

  // AI Settings Page – performance toggles
  'settings.ai.extendedContext': 'Extended Context (1M)',
  'settings.ai.extendedContextDescription': 'Use 1M token context window for Opus 4.6. Disable to use 200K and conserve usage limits.',
  'settings.ai.extendedPromptCache': 'Extended prompt cache (1 hour)',
  'settings.ai.extendedPromptCacheDescription': 'Cache prompts for 1 hour instead of 5 minutes. Only applies to Claude models via Anthropic API. Reduces cost for long sessions but increases cache write cost.',

  // AI Settings Page – workspace setting labels & errors
  'settings.ai.workspaceConnectionOverride': 'workspace connection override',
  'settings.ai.workspaceModelOverride': 'workspace model override',
  'settings.ai.workspaceThinkingOverride': 'workspace thinking override',
  'settings.ai.unknownError': 'Unknown error',
  'settings.ai.failedToSave': 'Failed to save {setting}',
  'settings.context': 'Memory & Docs',
  'settings.context.description': 'Manage user preferences, memory, and document sets',
  'context.memory': 'Memory',
  'context.docsets': 'Document sets',
  'context.globalMemory': 'Global memory',
  'context.projectMemory': 'Project memory',
  'context.memoryAutomation': 'Memory automation',
  'context.memoryRefineInterval': 'Auto-refine frequency',
  'context.memoryRefineIntervalDescription': 'After a conversation completes, check and refine enabled memory at this frequency.',
  'context.memoryRefineEveryTurn': 'Every completed conversation',
  'context.memoryRefineEveryMinutes': 'Every {minutes} minutes',
  'context.cloudEnhancement': 'Cloud semantic enhancement',
  'context.refineNow': 'Refine now',
  'context.clearMemory': 'Clear memory',
  'context.createDocset': 'New document set',
  'context.addFiles': 'Add files',
  'context.addUrl': 'Add URL',
  'context.rebuildIndex': 'Rebuild index',
  'context.moreActions': 'More actions',
  'context.noWorkspace': 'Select a workspace to manage project context.',
  'context.globalMemoryEnabled': 'Enable global memory',
  'context.projectMemoryEnabled': 'Enable project memory',
  'context.globalMemoryDescription': 'Shared Markdown memory injected into new sessions.',
  'context.projectMemoryDescription': 'Workspace Markdown memory injected into this project.',
  'context.globalMemoryPlaceholder': 'Write durable preferences, conventions, and facts shared across projects.',
  'context.projectMemoryPlaceholder': 'Write project conventions, paths, commands, and decisions for this workspace.',
  'context.cloudEnhancementDescription': 'Allow document sets to store cloud semantic metadata when enabled per document set.',
  'context.confirmCloudEnhancement': 'Cloud semantic enhancement may use external services in future integrations. Enable it?',
  'context.confirmDisableCloudEnhancement': 'Disable cloud semantic enhancement for all document sets?',
  'context.confirmDisableDocsetSemantic': 'Disable semantic enhancement for this document set and delete local semantic metadata?',
  'context.confirmClearMemory': 'Clear this memory file? This cannot be undone.',
  'context.confirmDeleteDocset': 'Delete this document set and its local index? This cannot be undone.',
  'context.createDocsetPrompt': 'Document set name',
  'context.addUrlPrompt': 'URL to import',
  'context.addUrlReading': 'Reading...',
  'context.addUrlReadingDescription': 'Reading page content and building the index...',
  'context.createDocsetFiles': 'Files',
  'context.createDocsetFilesEmpty': 'Select files now, or create an empty document set and add files later.',
  'context.selectFiles': 'Select files',
  'context.clearSelectedFiles': 'Clear',
  'context.memorySaved': 'Memory saved',
  'context.memoryCleared': 'Memory cleared',
  'context.memoryRefined': 'Memory refined, {count} item(s) extracted',
  'context.configSaved': 'Context settings saved',
  'context.docsetCreated': 'Document set created',
  'context.docsetCreatedWithFiles': 'Document set created and {count} file(s) indexed',
  'context.docsetDeleted': 'Document set deleted',
  'context.docsetUpdated': 'Document set updated',
  'context.filesAdded': 'Files added and indexed',
  'context.urlAdded': 'URL imported and indexed',
  'context.indexRebuilt': 'Index rebuilt',
  'context.actionFailed': 'Action failed: {message}',
  'context.emptyDocsets': 'No document sets yet.',
  'context.emptyDocsetFiles': 'No files in this document set yet.',
  'context.expandDocset': 'Expand document set',
  'context.collapseDocset': 'Collapse document set',
  'context.fileCount': '{count} file(s)',
  'context.totalSize': '{size}',
  'context.lastUpdated': 'Last updated {time}',
  'context.lastIndexed': 'Last indexed: {time}',
  'context.docsetIndexedFiles': 'Indexed {indexed} of {total} file(s)',
  'context.docsetChunkCount': '{count} chunk(s)',
  'context.documentErrors': 'Document errors',
  'context.semanticShort': 'Semantic',
  'context.semanticEnabled': 'Semantic enhancement',
  'context.semanticEnabledDescription': 'Mark this document set for cloud semantic metadata.',
  'context.status.ready': 'Ready',
  'context.status.indexing': 'Indexing',
  'context.status.partial': 'Partial',
  'context.status.failed': 'Failed',
  'context.status.stale': 'Stale',
  'context.documentStatus.pending': 'Pending',
  'context.documentStatus.converted': 'Converted',
  'context.documentStatus.indexed': 'Indexed',
  'context.documentStatus.failed': 'Failed',
  'context.documentStatus.skipped': 'Skipped',
  // ── Viewer ──────────────────────────────────────────────────────
  'viewer.clearSession': 'Clear session',
  'viewer.switchToLight': 'Switch to light mode',
  'viewer.switchToDark': 'Switch to dark mode',
  'viewer.uploadSession': 'Upload session JSON',
  'viewer.dropSessionHere': 'Drop session file here',
  'viewer.dragAndDrop': 'Drag and drop, click to browse, or paste from clipboard',
  'viewer.pleaseUploadJson': 'Please upload a JSON file',
  'viewer.invalidSessionFormat': 'Invalid session format: missing id or messages array',
  'viewer.failedToParseJson': 'Failed to parse JSON file',
  'viewer.processedLocally': 'Session files are processed locally in your browser.',
  'viewer.noDataUploaded': 'No data is uploaded to any server.',

  // ── Session management ──────────────────────────────────────────
  'session.addNewLabel': 'Add New Label',
  'session.editLabels': 'Edit Labels',
  'session.deleteLabel': 'Delete Label',
  'session.editViews': 'Edit Views',
  'session.deleteView': 'Delete View',
  'session.editConfiguration': 'Edit Configuration',
  'session.copyLink': 'Copy Link',
  'session.deleteSource': 'Delete Source',
  'session.addSource': 'Add Source',
  'session.addSkill': 'Add Skill',
  'session.addAutomation': 'Add Automation',
  'session.noArchived': 'No archived sessions',
  'session.noArchivedDesc': 'Sessions you archive will appear here. Archive sessions to keep your list tidy while preserving conversations.',
  'session.noSessionsYet': 'No sessions yet',
  'session.noSessionsYetDesc': 'Sessions with your agent appear here. Start one to get going.',
  'session.changeTodoState': 'Change todo state',
  'session.taskActions': 'Click for task actions',
  'session.delegationWaitingShort': 'Waiting {completed}/{total}',
  'session.delegationWaitingTooltip': 'Waiting for delegated sessions to finish ({completed}/{total} complete)',
  'session.delegationProgressTooltip': 'Delegated sessions complete ({completed}/{total})',

  // ── Dialogs ─────────────────────────────────────────────────────
  'dialog.resetApp': 'Reset App',
  'dialog.resetApp.description': 'This will <strong>permanently delete</strong>:',
  'dialog.resetApp.allWorkspaces': 'All workspaces and their settings',
  'dialog.resetApp.allCredentials': 'All credentials and API keys',
  'dialog.resetApp.allPreferences': 'All preferences and session data',
  'dialog.resetApp.backupWarning': 'Back up any important data first!',
  'dialog.resetApp.cannotUndo': 'This action cannot be undone.',
  'dialog.resetApp.confirmSolve': 'To confirm, solve: {a} + {b} =',
  'dialog.resetApp.enterAnswer': 'Enter answer',
  'dialog.deleteAutomation': 'Delete Automation',
  'dialog.deleteAutomation.description': 'Are you sure you want to delete this automation? This action cannot be undone.',
  'dialog.selectServerDirectory': 'Select Server Directory',
  'dialog.enterPath': 'Enter path...',
  'dialog.keyboardShortcuts': 'Keyboard Shortcuts',

  // ── Keyboard shortcuts ──────────────────────────────────────────
  'shortcuts.listNavigation': 'List Navigation',
  'shortcuts.navigateItems': 'Navigate items in list',
  'shortcuts.goToFirst': 'Go to first item',
  'shortcuts.goToLast': 'Go to last item',
  'shortcuts.sessionList': 'Session List',
  'shortcuts.focusChatInput': 'Focus chat input',
  'shortcuts.deleteSession': 'Delete session',
  'shortcuts.renameSession': 'Rename session',
  'shortcuts.openContextMenu': 'Open context menu',
  'shortcuts.addFilterExcluded': 'Add filter as excluded',
  'shortcuts.agentTree': 'Agent Tree',
  'shortcuts.collapseFolder': 'Collapse folder',
  'shortcuts.expandFolder': 'Expand folder',
  'shortcuts.chatInput': 'Chat Input',
  'shortcuts.sendMessage': 'Send message',
  'shortcuts.newLine': 'New line',
  'shortcuts.closeDialogBlur': 'Close dialog / blur input',

  // ── Action categories ──────────────────────────────────────────
  'actions.category.General': 'General',
  'actions.category.Navigation': 'Navigation',
  'actions.category.View': 'View',
  'actions.category.Navigator': 'Navigator',
  'actions.category.Chat': 'Chat',

  // ── Action labels ──────────────────────────────────────────────
  'actions.app.newChat': 'New Chat',
  'actions.app.newChatInPanel': 'New Chat in Panel',
  'actions.app.settings': 'Settings',
  'actions.app.toggleTheme': 'Toggle Theme',
  'actions.app.search': 'Search',
  'actions.app.keyboardShortcuts': 'Keyboard Shortcuts',
  'actions.app.newWindow': 'New Window',
  'actions.app.quit': 'Quit',
  'actions.nav.focusSidebar': 'Focus Sidebar',
  'actions.nav.focusNavigator': 'Focus Navigator',
  'actions.nav.focusChat': 'Focus Chat',
  'actions.nav.nextZone': 'Focus Next Zone',
  'actions.nav.goBack': 'Go Back',
  'actions.nav.goForward': 'Go Forward',
  'actions.nav.goBackAlt': 'Go Back',
  'actions.nav.goForwardAlt': 'Go Forward',
  'actions.nav.focusModePreviousSession': 'Previous Session in Focus Mode',
  'actions.nav.focusModeNextSession': 'Next Session in Focus Mode',
  'actions.view.toggleSidebar': 'Toggle Sidebar',
  'actions.view.toggleFocusMode': 'Toggle Focus Mode',
  'actions.navigator.selectAll': 'Select All',
  'actions.navigator.clearSelection': 'Clear Selection',
  'actions.panel.focusNext': 'Focus Next Panel',
  'actions.panel.focusPrev': 'Focus Previous Panel',
  'actions.chat.stopProcessing': 'Stop Processing',
  'actions.chat.cyclePermissionMode': 'Cycle Permission Mode',
  'actions.chat.nextSearchMatch': 'Next Search Match',
  'actions.chat.prevSearchMatch': 'Previous Search Match',

  // ── Formatting toolbar ──────────────────────────────────────────
  'format.bold': 'Bold',
  'format.italic': 'Italic',
  'format.strikethrough': 'Strikethrough',
  'format.code': 'Code',

  // ── App shell ───────────────────────────────────────────────────
  'appShell.craftMenu': 'Craft menu',
  'appShell.goBack': 'Go back',
  'appShell.goForward': 'Go forward',
  'appShell.toggleSidebar': 'Toggle Sidebar',
  'appShell.toggleSessionFilesSidebar': 'Toggle Files Sidebar',
  'appShell.addPanelMenu': 'Add panel menu',
  'appShell.helpDocumentation': 'Help & Documentation',
  'appShell.selectWorkspace': 'Select workspace',
  'appShell.removeWorkspace': 'Remove workspace',

  // ── Workspace ───────────────────────────────────────────────────
  'workspace.selectWorkspace': 'Select Workspace',
  'workspace.newWorkspaceName': 'New workspace name',
  'workspace.addWorkspace': 'Add Workspace',
  'workspace.createNew': 'Create new',
  'workspace.openFolder': 'Open folder',
  'workspace.connectRemote': 'Connect to remote server',
  'workspace.createWorkspace': 'Create workspace',
  'workspace.myWorkspace': 'My Workspace',
  'workspace.defaultLocation': 'Default location',
  'workspace.underCraftAgentFolder': 'under .okclawpro folder',
  'workspace.chooseLocation': 'Choose a location',
  'workspace.pickPlace': 'Pick a place to put your new workspace.',
  'workspace.chooseExistingFolder': 'Choose existing folder',
  'workspace.serverUrl': 'Server URL',
  'workspace.serverUrlPlaceholder': 'ws://192.168.1.100:9100',
  'workspace.serverToken': 'Server authentication token',
  'workspace.selectRemoteWorkspace': 'Select a workspace...',
  'workspace.myRemoteWorkspace': 'My Remote Workspace',

  // ── Onboarding ──────────────────────────────────────────────────
  'onboarding.setupAgent': 'Set up your Agent',
  'onboarding.setupDescription': 'Select how you\'d like to power your AI agents. You can add more connections later.',
  'onboarding.welcomeTo': 'Welcome to OKClaw Pro',
  'onboarding.connectChatGPT': 'Connect ChatGPT',
  'onboarding.connectCopilot': 'Connect GitHub Copilot',
  'onboarding.enterAuthCode': 'Enter Authorization Code',
  'onboarding.connectClaude': 'Connect Claude Account',
  'onboarding.apiConfiguration': 'API Configuration',
  'onboarding.localModel': 'Local Model',
  'onboarding.sessionExpired': 'Session Expired',
  'onboarding.claudeMax': 'Use your Claude subscription for unlimited access.',
  'onboarding.apiKey': 'Pay-as-you-go via Anthropic, OpenRouter, or compatible APIs.',
  'onboarding.chatgptPlus': 'Use your ChatGPT subscription with OKClaw Pro Backend.',
  'onboarding.copilot': 'Use your GitHub Copilot subscription with OKClaw Pro Backend.',
  'onboarding.piProvider': 'Use provider presets (Anthropic, OpenAI, Google, etc.) via OKClaw Pro Backend.',
  'onboarding.anthropicSub': 'Use your Anthropic subscription.',
  'onboarding.chatgptSub': 'Use your ChatGPT Plus or Pro subscription.',
  'onboarding.copilotSub': 'Use your GitHub Copilot subscription.',
  'onboarding.apiKeyProvider': 'Anthropic, AWS Bedrock, OpenRouter, Google or any compatible provider.',
  'onboarding.localModelDesc': 'Run models locally with Ollama.',
  'onboarding.ollamaUrl': 'http://localhost:11434',
  'onboarding.modelPlaceholder': 'e.g. qwen3-coder, llama3.3',
  'onboarding.gitBashRequired': 'Git Bash Required',
  'onboarding.gitBashPlaceholder': 'C:\\Program Files\\Git\\bin\\bash.exe',
  'onboarding.pasteAuthCode': 'Paste your authorization code here',

  // ── Automations ─────────────────────────────────────────────────
  'automations.noAutomations': 'No automations configured',
  'automations.searchPlaceholder': 'Search automations...',
  'automations.when': 'When',
  'automations.if': 'If',
  'automations.then': 'Then',
  'automations.recentActivity': 'Recent Activity',
  'automations.rawConfig': 'Raw config',
  'automations.everyMinute': 'Every minute',
  'automations.everyMinuteDesc': 'Runs every minute',
  'automations.every15min': 'Every 15 min',
  'automations.every15minDesc': 'Runs every 15 minutes',
  'automations.everyHour': 'Every hour',
  'automations.everyHourDesc': 'At the top of every hour',
  'automations.dailyMidnight': 'Daily at midnight',
  'automations.dailyMidnightDesc': 'Once a day at 00:00',
  'automations.daily9am': 'Daily at 9am',
  'automations.daily9amDesc': 'Once a day at 09:00',
  'automations.weekdays9am': 'Weekdays at 9am',
  'automations.weekdays9amDesc': 'Monday–Friday at 09:00',
  'automations.monthlyFirst': 'Monthly on 1st',
  'automations.monthlyFirstDesc': 'First day of each month at 00:00',
  'automations.summary.nextRun': 'Next run: {formatted} ({timezone})',
  'automations.summary.cron': 'Cron: {cron}{timezoneSuffix}',
  'automations.summary.matches': 'Matches: {matcher}',
  'automations.summary.on': 'On {event}',
  'automations.summary.promptAction': '{mention} prompt',
  'automations.channel.feishu': 'Feishu',
  'automations.channel.dingtalk': 'DingTalk',
  'automations.channel.wecom': 'WeCom',
  'automations.channel.qqbot': 'QQ',
  'automations.channel.weixinV2': 'Weixin',
  'automations.channel.mail': 'Email',
  'automations.status.active': 'Active',
  'automations.status.disabled': 'Disabled',
  'automations.info.pausedTitle': 'Paused',
  'automations.info.pausedDescription': 'This automation is turned off. Enable it to start running again.',
  'automations.info.triggerDescription': 'What causes this automation to run',
  'automations.info.conditionsDescription': 'Conditions that must pass before actions run',
  'automations.info.actionsCount': '{count} action(s) to perform',
  'automations.info.settings': 'Settings',
  'automations.info.accessLevel': 'Access Level',
  'automations.info.status': 'Status',
  'automations.info.labels': 'Labels',
  'automations.info.event': 'Event',
  'automations.info.timing': 'Timing',
  'automations.info.onlyWhenMatching': 'Only when matching',
  'automations.info.repeats': 'Repeats',
  'automations.info.scheduleExpression': 'Schedule expression',
  'automations.info.nextRuns': 'Next runs',
  'automations.info.timezone': 'Timezone',
  'automations.info.systemDefault': 'System default',
  'automations.info.lastRuns': 'Last {count} runs',
  'automations.info.noActivityYet': 'No activity yet.',
  'automations.info.openSession': 'Open session',
  'automations.info.method': 'Method',
  'automations.info.url': 'URL',
  'automations.info.duration': 'Duration',
  'automations.info.attempts': 'Attempts',
  'automations.info.error': 'Error',
  'automations.info.response': 'Response',
  'automations.info.defaultTarget': 'default target',
  'automations.info.noResponse': 'No response',

  // ── Source status ───────────────────────────────────────────────
  'source.connected': 'Connected',
  'source.connectedDesc': 'Source is connected and working',
  'source.needsAuth': 'Needs Authentication',
  'source.needsAuthDesc': 'Source requires authentication to connect',
  'source.connectionFailed': 'Connection Failed',
  'source.connectionFailedDesc': 'Failed to connect to source',
  'source.notTested': 'Not Tested',
  'source.disabled': 'Disabled',
  'source.disabledDesc': 'Local MCP servers are disabled in Settings',

  // ── Info pages ──────────────────────────────────────────────────
  'info.connection': 'Connection',
  'info.permissions': 'Permissions',
  'info.tools': 'Tools',
  'info.documentation': 'Documentation',
  'info.metadata': 'Metadata',
  'info.permissionModes': 'Permission Modes',
  'info.instructions': 'Instructions',
  'info.name': 'Name',
  'info.type': 'Type',
  'info.access': 'Access',
  'info.pattern': 'Pattern',
  'info.label': 'Label',
  'info.tool': 'Tool',

  // ── Mention / Slash command menus ───────────────────────────────
  'mention.results': 'Results',
  'mention.skills': 'Skills',
  'mention.sources': 'Sources',
  'mention.files': 'Files',
  'slash.compactContext': 'Compact Context',
  'slash.compactContextDesc': 'Summarize conversation context to free up token budget',
  'slash.modes': 'Modes',
  'slash.commands': 'Commands',
  'slash.recentWorkingDirs': 'Recent Working Directories',
  'slash.searchCommands': 'Search commands...',
  'slash.noCommandsFound': 'No commands found',
  'slash.atHint': 'Use @ for skills and files',

  // ── Edit popover labels ─────────────────────────────────────────
  'editPopover.permissionSettings': 'Permission Settings',
  'editPopover.defaultPermissions': 'Default Permissions',
  'editPopover.skillInstructions': 'Skill Instructions',
  'editPopover.skillMetadata': 'Skill Metadata',
  'editPopover.sourceDocumentation': 'Source Documentation',
  'editPopover.sourceConfiguration': 'Source Configuration',
  'editPopover.sourcePermissions': 'Source Permissions',
  'editPopover.toolPermissions': 'Tool Permissions',
  'editPopover.preferencesNotes': 'Preferences Notes',
  'editPopover.addSource': 'Add Source',
  'editPopover.addApi': 'Add API',
  'editPopover.addMcpServer': 'Add MCP Server',
  'editPopover.addLocalFolder': 'Add Local Folder',
  'editPopover.addSkill': 'Add Skill',
  'editPopover.statusConfiguration': 'Status Configuration',
  'editPopover.labelConfiguration': 'Label Configuration',
  'editPopover.autoApplyRules': 'Auto-Apply Rules',
  'editPopover.addLabel': 'Add Label',
  'editPopover.viewsConfiguration': 'Views Configuration',
  'editPopover.toolIcons': 'Tool Icons',
  'editPopover.automationConfiguration': 'Automation Configuration',

  // ── Label value popover ─────────────────────────────────────────
  'labelValue.datePlaceholder': 'tomorrow, next friday...',
  'labelValue.selectDate': 'Select date',
  'labelValue.numberPlaceholder': 'Enter number...',
  'labelValue.textPlaceholder': 'Enter value...',
  'labelValue.filterStatuses': 'Filter statuses...',

  // ── API setup ───────────────────────────────────────────────────
  'apiSetup.searchModels': 'Search models...',
  'apiSetup.customModelPlaceholder': 'e.g. claude-opus-4-6, claude-sonnet-4-6, claude-haiku-4-5',
  'apiSetup.baseUrlPlaceholder': 'https://your-api-endpoint.com',
  'apiSetup.awsAccessKey': 'AKIA...',
  'apiSetup.awsSecretKey': 'Your secret access key',
  'apiSetup.awsSessionToken': 'For temporary credentials (STS)',
  'apiSetup.awsRegion': 'us-east-1',

  // ── Auth request ────────────────────────────────────────────────
  'auth.connected': '{source} Connected',
  'auth.cancelled': '{source} Cancelled',
  'auth.failed': '{source} Failed',
  'auth.authentication': '{source} Authentication',

  // ── Misc ────────────────────────────────────────────────────────
  'misc.plan': 'Plan',
  'misc.mermaidDiagram': 'Mermaid Diagram',
  'misc.math': 'Math',
  'misc.tableControls': 'Table Controls',
  'misc.nLabels': '{count} labels',
  'misc.oneLabel': '1 label',
  'misc.nRules': '{count} rules',
  'misc.oneRule': '1 rule',
  'misc.image': 'Image',
  'misc.diagram': 'Diagram',

  // ── Sidebar navigation ──────────────────────────────────────────
  'nav.newSession': 'New Session',
  'nav.allSessions': 'All Sessions',
  'nav.flagged': 'Flagged',
  'nav.archived': 'Archived',
  'nav.labels': 'Labels',
  'nav.sources': 'Sources',
  'nav.apis': 'APIs',
  'nav.mcps': 'MCPs',
  'nav.localFolders': 'Local Folders',
  'nav.skills': 'Skills',
  'nav.automations': 'Automations',
  'nav.scheduled': 'Scheduled',
  'nav.eventBased': 'Event-based',
  'nav.agentic': 'Agentic',
  'nav.settings': 'Settings',
  'nav.share': 'Share',
  'nav.shareLinkCopied': 'Invitation link copied',
  'nav.shareCopyFailed': 'Copy failed, please try again',
  'nav.shareNoInviteCode': 'No invitation code available',
  'nav.whatsNew': 'What\'s New',
  'nav.whatsNewEmpty': 'No announcements yet',
  'nav.openInNewWindow': 'Open in New Window',
  'nav.back': 'Back',
  'nav.forward': 'Forward',
  'nav.markAllRead': 'Mark All Read',
  'nav.configureStatuses': 'Configure Statuses',
  'nav.learnMoreApis': 'Learn More about APIs',
  'nav.learnMoreMcp': 'Learn More about MCP',
  'nav.learnMoreLocalFolders': 'Learn More about Local Folders',
  'nav.learnMoreSources': 'Learn More about Sources',
  'nav.learnMoreAutomations': 'Learn More about Automations',
  'nav.newSessionInPanel': 'New Session in Panel',
  'nav.newBrowserWindow': 'New Browser Window',
  'nav.toggleDevTools': 'Toggle DevTools',
  'nav.allDocumentation': 'All Documentation',

  // ── Session list ────────────────────────────────────────────────
  'sessionList.today': 'Today',
  'sessionList.yesterday': 'Yesterday',
  'sessionList.inCurrentView': 'In Current View',
  'sessionList.otherConversations': 'Other Conversations',
  'sessionList.noResultsInFilter': 'No results in current filter',
  'sessionList.noSessionsFound': 'No sessions found',
  'sessionList.searchedContent': 'Searched titles and message content',
  'sessionList.clearSearch': 'Clear search',

  // ── App settings detail items ───────────────────────────────────
  'appSettings.desktopNotifications': 'Desktop notifications',
  'appSettings.desktopNotificationsDesc': 'Get notified when AI finishes working in a chat.',
  'appSettings.preventComputerSleep': 'Prevent computer sleep',
  'appSettings.preventComputerSleepDesc': 'Keep the computer awake while sessions are running so tasks can continue in the background. The screen may still turn off.',
  'appSettings.preventComputerSleepDisableTitle': 'Turn off sleep prevention?',
  'appSettings.preventComputerSleepDisableDesc': 'If the computer goes to sleep, connected tools such as WeChat and Feishu will no longer be able to keep replying to messages.',
  'appSettings.turnOffPreventComputerSleep': 'Turn Off',
  'appSettings.builtInBrowser': 'Built-in browser',
  'appSettings.builtInBrowserDesc': 'Disable if you use external browser tools like Playwright, Puppeteer, or browser MCP servers.',
  'appSettings.httpProxy': 'HTTP proxy',
  'appSettings.httpProxyDesc': 'Route network traffic through a proxy server.',
  'appSettings.httpProxyLabel': 'HTTP Proxy',
  'appSettings.httpsProxyLabel': 'HTTPS Proxy',
  'appSettings.bypassRules': 'Bypass Rules',
  'appSettings.reset': 'Reset',
  'appSettings.saving': 'Saving...',
  'appSettings.checkNow': 'Check Now',
  'appSettings.checking': 'Checking...',
  'appSettings.version': 'Version',
  'appSettings.checkForUpdates': 'Check for updates',
  'appSettings.updateAvailable': 'Update available',
  'appSettings.downloadUpdate': 'Download v{version}',
  'appSettings.updateReady': 'Update ready',
  'appSettings.loadingVersion': 'Loading...',
  'appSettings.downloading': 'Downloading...',
  'appSettings.restartToUpdate': 'Restart to Update to v{version}',
  'appSettings.proxyUrlProtocolError': 'Must be http://, https://, socks4://, or socks5:// URL',
  'appSettings.proxyUrlInvalid': 'Invalid URL format',
  'appSettings.proxyPlaceholder': 'http://proxy.example.com:8080',
  'appSettings.bypassPlaceholder': 'localhost, 127.0.0.1, .example.com',
  'appSettings.account': 'Account',
  'appSettings.logout': 'Log Out',
  'appSettings.logoutDesc': 'Log out of your OKClaw Pro account and return to the login page.',
  'appSettings.loggingOut': 'Logging out...',

  // ── Subscription settings ──────────────────────────────────────
  'settings.subscription': 'Subscription',
  'settings.subscription.description': 'Plans, balance, and API keys',
  'subscription.balance': 'Account Balance',
  'subscription.balanceAmount': '¥{amount}',
  'subscription.refreshBalance': 'Refresh',
  'subscription.plans': 'My Subscriptions',
  'subscription.noSubscriptions': 'No active subscriptions',
  'subscription.expired': 'Expired',
  'subscription.expiring': 'Expiring Soon',
  'subscription.active': 'Active',
  'subscription.expiresOn': 'Expires: {date}',
  'subscription.currentCycle': 'Current cycle: {start} to {end}',
  'subscription.traffic': 'Traffic',
  'subscription.trafficUsage': '{remaining} / {total}',
  'subscription.apiKeys': 'API Keys',
  'subscription.noKeys': 'No API keys',
  'subscription.currentKey': 'Current',
  'subscription.setAsCurrent': 'Set as Current',
  'subscription.switchSubscription': 'Switch Subscription',
  'subscription.renew': 'Renew',
  'subscription.buyTraffic': 'Buy Quota Package',
  'subscription.buyPlan': 'Buy Plan',
  'subscription.buyFormalPlan': 'Buy Regular Plan',
  'subscription.refreshing': 'Refreshing...',
  'subscription.keySetupSuccess': 'API key configured successfully',
  'subscription.keySetupFailed': 'Failed to configure API key',
  'subscription.switchingCurrent': 'Switching to "{name}"...',
  'subscription.currentSwitchSuccess': 'Switched to "{name}"',
  'subscription.noAvailableKeyForPlan': 'This plan is not available. Make sure it is not expired, still has quota, and has an API key.',
  'subscription.autoSetup': 'Auto Setup',
  'subscription.autoSetupDesc': 'Automatically select the best available API key and configure AI connections.',
  'subscription.autoSetupRunning': 'Setting up...',
  'subscription.connections': 'AI Connections',
  'subscription.connectionsDesc': 'These connections are automatically managed by your subscription.',
  'subscription.newSubscription': 'New Subscription',
  'subscription.selectPlan': 'Select Plan',
  'subscription.renewQuotaNotice': 'Renewal purchases quota for the next cycle and does not increase the current cycle quota. To increase the current cycle quota, click Buy Quota Package.',
  'subscription.selectDuration': 'Select Duration',
  'subscription.month': 'Monthly',
  'subscription.quarter': 'Quarterly',
  'subscription.year': 'Yearly',
  'subscription.quantity': 'Quantity',
  'subscription.totalPrice': 'Total',
  'subscription.creating': 'Creating...',
  'subscription.createOrder': 'Create Order',
  'subscription.monthlyQuota': 'Monthly quota: {value}',
  'subscription.benefits': 'Member Benefits',
  'subscription.cashier': 'Checkout',
  'subscription.orderNumber': 'Order',
  'subscription.orderAmount': 'Amount',
  'subscription.discount': 'Discount',
  'subscription.payable': 'Payable',
  'subscription.coupon': 'Coupon',
  'subscription.noCoupon': 'No coupon',
  'subscription.payMethod': 'Payment Method',
  'subscription.wechatPay': 'WeChat Pay',
  'subscription.alipay': 'Alipay',
  'subscription.balancePay': 'Balance',
  'subscription.paying': 'Paying...',
  'subscription.confirmPay': 'Pay Now',
  'subscription.scanToPay': 'Scan QR code to pay',
  'subscription.scanWithMethod': 'Scan with {method} to pay',
  'subscription.changePayMethod': 'Change Payment Method',
  'subscription.pollingPayment': 'Waiting for payment...',
  'subscription.paymentComplete': 'Payment Complete',
  'subscription.paySuccess': 'Payment Successful',
  'subscription.payFailed': 'Payment Failed',
  'subscription.useBalance': 'Use Balance',
  'subscription.useAccountBalance': 'Use account balance (Current: ¥{amount})',
  'subscription.balanceInsufficient': 'Insufficient balance',
  'subscription.balanceSufficient': 'Balance is sufficient to cover the order',
  'subscription.selectPayMethod': 'Payment Method',
  'subscription.settlement': 'Settlement',
  'subscription.orderAmountLabel': 'Order Amount',
  'subscription.couponDiscount': 'Coupon Discount',
  'subscription.balanceDeduction': 'Balance Deduction',
  'subscription.thirdPartyAmount': '{method} Payment',
  'subscription.actualPayment': 'Actual Payment',
  'subscription.paymentPending': 'Order submitted, please pay soon!',
  'subscription.paymentProcessing': 'Payment processing...',
  'subscription.paymentSuccess': 'Payment successful',
  'subscription.orderExpired': 'Order expired',
  'subscription.payBefore': 'Please pay before {time}, or the order will be cancelled automatically.',
  'subscription.balancePayOnly': 'Pay with Balance',
  'subscription.autoKeyFetching': 'Initializing AI service, please wait...',
  'subscription.initializingHint': 'The server is creating your API key and configuring AI models. This usually takes 10-30 seconds.',
  'subscription.autoKeySuccess': 'AI service configured successfully!',
  'subscription.autoClosing': 'This dialog will close automatically...',
  'subscription.autoKeyTimeout': 'Initialization timed out. Please go to Settings → Subscription and click "Auto Configure".',
  'subscription.countdown': 'Time remaining',
  'subscription.discountOptions': 'Discounts',
  'subscription.refreshDiscount': 'Refresh',
  'subscription.studentDiscount': 'Student Discount',
  'subscription.studentDiscountLabel': 'Student Exclusive',
  'subscription.noDiscount': 'No discount',
  'subscription.couponSelected': 'Selected',
  'subscription.couponThreshold': 'Min. ¥{amount}',
  'subscription.couponNoThreshold': 'No minimum',
  'subscription.yuan': 'CNY',
  'subscription.selectPackage': 'Select Quota Package',
  'subscription.loading': 'Loading...',
  'subscription.confirm': 'OK',
  'subscription.fetchPackagesFailed': 'Failed to load packages',
  'subscription.createOrderFailed': 'Failed to create order',
  'subscription.buyTrafficDesc': 'Select a quota package to purchase',
  'subscription.trafficPackageCycleNoticeTitle': 'Quota packages are valid only for the current cycle',
  'subscription.trafficPackageCycleNoticeDesc': 'Purchased quota packages are added to the current OKClaw instance cycle and expire with the instance at the current cycle end time. They do not carry over to the renewed cycle.',
  'subscription.trafficPackageValidUntil': 'This quota purchase is valid until:',
  'subscription.confirmPurchase': 'Confirm Purchase',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.saving': 'Saving...',
  'common.testing': 'Testing...',
  'common.start': 'Start',
  'common.stop': 'Stop',
  'common.refresh': 'Refresh',
  'common.delete': 'Delete',
  'common.copied': 'Copied',

  // ── Permission request / Structured input ───────────────────────
  'permission.allow': 'Allow',
  'permission.allowAlways': 'Allow Always',
  'permission.deny': 'Deny',
  'permission.allowOnce': 'Allow Once',
  'permission.required': 'Permission required',
  'permission.tool': 'Tool:',
  'permission.alwaysAllowHint': '"Always Allow" remembers this command for the session',

  // ── Credential request ──────────────────────────────────────────
  'credential.authRequired': 'Authentication Required',
  'credential.bearerToken': 'Bearer Token',
  'credential.apiKey': 'API Key',
  'credential.username': 'Username',
  'credential.password': 'Password',
  'credential.enterPlaceholder': 'Enter {field}',
  'credential.save': 'Save',
  'credential.cancel': 'Cancel',
  'credential.encryptedAtRest': 'Credentials are encrypted at rest',

  // ── Admin approval request ──────────────────────────────────────
  'admin.approvalRequired': 'Admin approval required',
  'admin.installNeedsApproval': 'Installing {appName} needs your Mac admin approval.',
  'admin.macOsPrompt': "You'll see your regular macOS password/Touch ID prompt.",
  'admin.why': 'Why:',
  'admin.impact': 'Impact:',
  'admin.approve': 'Approve',
  'admin.cancel': 'Cancel',
  'admin.rememberFor': 'Remember for {minutes} min',

  // ── Overlay / Preview extras ────────────────────────────────────
  'overlay.openMermaidFullscreen': 'Open Mermaid diagram fullscreen',
  'overlay.copyPath': 'Copy path',
  'overlay.codeLanguage': 'Code language',
  'overlay.selectCodeLanguage': 'Select code language',
  'overlay.searchLanguages': 'Search languages...',

  // ── Chat input ──────────────────────────────────────────────────
  'chatInput.sendMessage': 'Send message',
  'chatInput.newLine': 'New line',
  'chatInput.placeholder': 'Message...',
  'chatInput.attachFiles': 'Attach files',
  'chatInput.attachFilesLabel': 'Attach Files',
  'chatInput.chooseFileSource': 'Choose file source',
  'chatInput.chooseFileSourceTooltip': 'Choose files from this browser computer or the OKClaw client computer',
  'chatInput.localDeviceFiles': 'Browser computer files',
  'chatInput.serverDeviceFiles': 'OKClaw client computer files',
  'chatInput.failedAttachServerFile': 'Failed to attach file from OKClaw client computer',
  'chatInput.oneFile': '1 file',
  'chatInput.nFiles': '{count} files',
  'chatInput.chooseSources': 'Choose Sources',
  'chatInput.nSources': '{count} sources',
  'chatInput.sourcesTooltip': 'Sources',
  'chatInput.workInFolder': 'Work in Folder',
  'chatInput.folder': 'Folder',
  'chatInput.workingDirectory': 'Working directory',
  'chatInput.chooseWorkingDirectory': 'Choose working directory',
  'chatInput.filterFolders': 'Filter folders...',
  'chatInput.noFoldersFound': 'No folders found',
  'chatInput.chooseFolder': 'Choose Folder...',
  'chatInput.reset': 'Reset',
  'chatInput.model': 'Model',
  'chatInput.unavailable': 'Unavailable',
  'chatInput.connectionUnavailable': 'Connection Unavailable',
  'chatInput.connectionUnavailableDesc': 'The connection used by this session has been removed. Create a new session to continue.',
  'chatInput.connectionDefault': 'Connection default',
  'chatInput.notAuthenticated': 'Not authenticated',
  'chatInput.officialChannels': 'Official Channels',
  'chatInput.thirdPartyChannels': 'Third-party Channels',
  'chatInput.connectionLockedToCurrent': 'Session locked to the current channel',
  'chatInput.connectionProtocolLocked': 'This session cannot switch to a different API format',
  'chatInput.thinkingNotSupported': 'Not supported by this model',
  'chatInput.extendedReasoning': 'Extended reasoning depth',
  'chatInput.context': 'Context',
  'chatInput.tokensUsed': '{count} tokens used',
  'chatInput.contextUsedWait': '{percent}% context used — wait for current operation',
  'chatInput.contextUsedCompact': '{percent}% context used — click to compact',
  'chatInput.usingConnection': 'Using {name}',
  'chatInput.pasteTextConfirmTitle': 'Paste Long Text',
  'chatInput.pasteTextConfirmDescription': 'The pasted content is about {lines} lines. Insert it into the input box, or send it as a text file attachment.',
  'chatInput.pasteLargeTextConfirmDescription': 'The pasted content is about {lines} lines. Sending it as a text file attachment is recommended. If you choose to insert the text, the app needs to write it into the input box and may take a moment.',
  'chatInput.pasteAsText': 'Insert Text',
  'chatInput.pasteAsAttachment': 'Attach as File',
  // Default rotating placeholders
  'chatInput.placeholder.workOn': 'What would you like to work on?',
  'chatInput.placeholder.shiftTab': 'Use Shift + Tab to switch between Explore and Execute',
  'chatInput.placeholder.mentionFiles': 'Type @ to mention files, folders, or skills',
  'chatInput.placeholder.applyLabels': 'Type # to apply labels to this conversation',
  'chatInput.placeholder.newLine': 'Press Shift + Return to add a new line',
  'chatInput.placeholder.toggleSidebar': 'Press {cmdKey} + B to toggle the sidebar',
  'chatInput.placeholder.focusMode': 'Press {cmdKey} + . for focus mode',
  'chatInput.tipsPrefix': 'Tips:',

  // ── Toolbar status slot ─────────────────────────────────────────
  'toolbar.pressEscPrefix': 'Press',
  'toolbar.pressEscSuffix': 'again to interrupt',
  'toolbar.usingHostname': 'Using {hostname}',

  // ── Session menu parts ──────────────────────────────────────────
  'sessionMenu.moveTo': 'Move to...',
  'sessionMenu.forkSession': 'Fork Session',
  'sessionMenu.shareSession': 'Share Session',
  'sessionMenu.exportSession': 'Export Session',
  'sessionMenu.deleteSession': 'Delete Session',
  'sessionMenu.pin': 'Pin',
  'sessionMenu.unpin': 'Unpin',
  'sessionMenu.flag': 'Flag',
  'sessionMenu.unflag': 'Unflag',
  'sessionMenu.archive': 'Archive',
  'sessionMenu.unarchive': 'Unarchive',
  'sessionMenu.share': 'Share',
  'sessionMenu.shared': 'Shared',
  'sessionMenu.sendToWorkspace': 'Send to Workspace...',
  'sessionMenu.status': 'Status',
  'sessionMenu.labels': 'Labels',
  'sessionMenu.markAsUnread': 'Mark as Unread',
  'sessionMenu.rename': 'Rename',
  'sessionMenu.regenerateTitle': 'Regenerate Title',
  'sessionMenu.repairSession': 'Repair Session',
  'sessionMenu.openInNewPanel': 'Open in New Panel',
  'sessionMenu.openInNewWindow': 'Open in New Window',
  'sessionMenu.showInFileManager': 'Show in {fileManager}',
  'sessionMenu.copyPath': 'Copy Path',
  'sessionMenu.linkCopied': 'Link copied to clipboard',
  'sessionMenu.failedToShare': 'Failed to share',
  'sessionMenu.pathCopied': 'Path copied to clipboard',
  'sessionMenu.titleRefreshed': 'Title refreshed',
  'sessionMenu.failedToRefreshTitle': 'Failed to refresh title',
  'sessionMenu.sessionRepaired': 'Session repaired',
  'sessionMenu.sessionRepairedResumeReset': 'Resume state reset. The next message will restore context from local history.',
  'sessionMenu.sessionRepairedMetadata': 'Session file rewritten.',
  'sessionMenu.sessionRepairedNetworkErrors': 'Removed {count} trailing network error records and rewrote the session file.',
  'sessionMenu.failedToRepairSession': 'Failed to repair session',

  // ── Share menu ──────────────────────────────────────────────────
  'share.openInBrowser': 'Open in Browser',
  'share.updateShare': 'Update Share',
  'share.stopSharing': 'Stop Sharing',
  'share.linkCopied': 'Link copied to clipboard',
  'share.updated': 'Share updated',
  'share.failedUpdate': 'Failed to update share',
  'share.stopped': 'Sharing stopped',
  'share.failedStop': 'Failed to stop sharing',
  'share.publicShare': 'Public Share',
  'share.passwordShare': 'Share Now',
  'share.passwordLabel': 'Password',
  'share.changeTypeHint': 'Shared links are password protected.',
  'share.dialogTitle': 'Share Conversation',
  'share.updateDialogTitle': 'Update Share',
  'share.dialogDescription': 'Choose the conversation rounds to include. Only selected rounds are uploaded.',
  'share.uploadConsent': 'I understand and agree to upload the selected conversation to the cloud',
  'share.consentRequired': 'Please confirm the upload consent before creating a share',
  'share.turnsTitle': 'Conversation rounds',
  'share.selectedTurns': '{selected}/{total} selected',
  'share.selectAll': 'Select all',
  'share.deselectAll': 'Deselect all',
  'share.turnNumber': 'Round {number}',
  'share.turnMessageCount': '{count} messages',
  'share.customPassword': 'Custom password',
  'share.customPasswordPlaceholder': 'Leave blank to generate automatically',
  'share.customPasswordHint': 'Up to 32 letters or numbers. The backend stores only the encrypted password hash.',
  'share.passwordTooLong': 'Password cannot exceed 32 characters.',
  'share.passwordInvalidCharacters': 'Password can only contain uppercase letters, lowercase letters, and numbers.',
  'share.createShare': 'Create share',
  'share.submitting': 'Sharing...',
  'share.failedLoadSession': 'Failed to load conversation',
  'share.noShareableTurns': 'No shareable conversation rounds',
  'share.noSelectedTurns': 'Select at least one conversation round',
  'share.publicShareUnsupported': 'Public links are no longer supported. Stop sharing first, then create a password-protected share.',

  // ── Skill & source info ─────────────────────────────────────────
  'skillInfo.noSkills': 'No skills configured',
  'skillInfo.description': 'Skills are reusable instructions that teach your agent specialized behaviors.',
  'skillInfo.addSkill': 'Add Skill',
  'skillInfo.importSkill': 'Import Skill',
  'skillInfo.marketplace': 'Skills Marketplace',
  'skillInfo.importSkillTitle': 'Select a skill folder to import',
  'skillInfo.bundled': 'bundled',
  'skillInfo.bundledReadOnly': 'Bundled with app (read-only)',
  'skillInfo.project': 'project',
  'skillInfo.deleteSkill': 'Delete Skill',
  'skillInfo.managedByProject': 'Managed by project',
  'skillMarketplace.title': 'Skills Marketplace',
  'skillMarketplace.description': 'Browse and install official skills.',
  'skillMarketplace.searchPlaceholder': 'Search skills',
  'skillMarketplace.allCategories': 'All categories',
  'skillMarketplace.install': 'Install',
  'skillMarketplace.installGlobal': 'Install globally',
  'skillMarketplace.installWorkspace': 'Install to workspace',
  'skillMarketplace.installGlobalShort': 'Global',
  'skillMarketplace.installWorkspaceShort': 'Workspace',
  'skillMarketplace.installed': 'Installed',
  'skillMarketplace.installSuccess': 'Installed {name}',
  'skillMarketplace.installError': 'Failed to install skill',
  'skillMarketplace.loading': 'Loading...',
  'skillMarketplace.loadError': 'Failed to load skills marketplace',
  'skillMarketplace.noResults': 'No skills found',
  'skillMarketplace.noResultsDescription': 'Try another search term or category.',
  'skillMarketplace.unavailable': 'Marketplace unavailable',
  'skillMarketplace.unavailableDescription': 'This server does not expose the marketplace endpoints yet.',
  'skillMarketplace.pageStatus': 'Page {page} of {totalPages}',
  'skillMarketplace.previousPage': 'Previous page',
  'skillMarketplace.nextPage': 'Next page',
  'skillMarketplace.selectSkill': 'Select a skill to view details',
  'skillMarketplace.selectSkillDescription': 'Click a skill in the list to see its full description and install actions here.',
  'skillMarketplace.fullDescription': 'Full description',
  'skillMarketplace.noDescription': 'No description yet.',
  'skillMarketplace.details': 'Details',
  'skillMarketplace.category': 'Category',
  'skillMarketplace.skillCode': 'Identifier',
  'skillMarketplace.lastPublished': 'Published',
  'skillMarketplace.branch': 'Branch',
  'tray.show': 'Show OKClaw Pro',
  'tray.quit': 'Quit OKClaw Pro',
  'tray.stillRunningTitle': 'OKClaw Pro is still running',
  'tray.stillRunningBody': 'Click the tray icon to reopen the window. Use the tray menu "Quit" to exit completely.',
  'sourceInfo.noSources': 'No sources configured.',
  'sourceInfo.noTypeSources': 'No {type} sources configured.',
  'sourceInfo.description': 'Sources connect your agent to external data — MCP servers, REST APIs, and local folders.',
  'sourceInfo.addSource': 'Add Source',
  'sourceInfo.typeMcp': 'MCP',
  'sourceInfo.typeApi': 'API',
  'sourceInfo.typeLocal': 'Local',
  'sourceInfo.statusAuthRequired': 'Auth Required',
  'sourceInfo.statusDisconnected': 'Disconnected',
  'sourceInfo.statusNotTested': 'Not Tested',
  'sourceInfo.statusDisabled': 'Disabled',

  // ── Info table columns ─────────────────────────────────────────
  'info.noToolsAvailable': 'No tools available',
  'info.descriptionColumn': 'Description',

  // ── Onboarding extras ───────────────────────────────────────────
  'onboarding.getStarted': 'Get Started',
  'onboarding.next': 'Next',
  'onboarding.skip': 'Skip',
  'onboarding.connecting': 'Connecting...',
  'onboarding.validating': 'Validating...',
  'onboarding.connected': 'Connected!',
  'onboarding.settingUp': 'Setting up...',
  'onboarding.allSet': "You're all set!",
  'onboarding.savingConfig': 'Saving your configuration...',
  'onboarding.startChatting': 'Just start a chat and get to work.',
  'onboarding.updateSettings': 'Update Settings',
  'onboarding.updateDescription': 'Update your API connection or change your setup.',
  'onboarding.welcomeDescription': 'Agents with the UX they deserve. Connect anything. Organize your sessions. Everything you need to do the work of your life!',
  'onboarding.checking': 'Checking...',

  // ── Workspace extras ────────────────────────────────────────────
  'workspace.connecting': 'Connecting...',
  'workspace.testConnection': 'Test Connection',
  'workspace.reconnect': 'Reconnect',

  // ── Multi-select / batch ────────────────────────────────────────
  'batch.deleteSelected': 'Delete Selected',
  'batch.archiveSelected': 'Archive Selected',
  'batch.moveSelected': 'Move Selected',
  'batch.nSelected': '{count} selected',
  'batch.status': 'Status',
  'batch.labels': 'Labels',
  'batch.flagAll': 'Flag All',
  'batch.unflagAll': 'Unflag All',
  'batch.archive': 'Archive',
  'batch.sendToWorkspace': 'Send to Workspace...',
  'batch.delete': 'Delete',
  'batch.sessionsSelected': '{count} {noun} selected',
  'batch.sessionsFlagged': '{count} {noun} flagged',
  'batch.sessionsUnflagged': '{count} {noun} unflagged',
  'batch.sessionsArchived': '{count} {noun} archived',
  'batch.sessionsDeleted': '{count} {noun} deleted',

  // ── Multi-select panel ──────────────────────────────────────────
  'multiSelect.selected': '{count} {noun} selected',
  'multiSelect.useToggle': 'to toggle,',
  'multiSelect.useRange': 'for range',
  'multiSelect.pressEscPrefix': 'Press',
  'multiSelect.pressEscSuffix': 'to clear selection',
  'multiSelect.changeStatus': 'Change Status',
  'multiSelect.setLabels': 'Set Labels',
  'multiSelect.archive': 'Archive',

  // ── Markdown / Rich text extras ─────────────────────────────────
  'markdown.openInBrowser': 'Open in Browser',
  'markdown.download': 'Download',
  'markdown.expand': 'Expand',
  'markdown.collapse': 'Collapse',
  'markdown.showMore': 'Show more',
  'markdown.showLess': 'Show less',
  'markdown.editBlock': 'Edit block',
  'markdown.noLanguagesFound': 'No languages found',
  'markdown.plainText': 'Plain text',
  'markdown.math': 'Math',
  'markdown.copyCode': 'Copy code',

  // ── SendToWorkspace dialog ──────────────────────────────────────
  'sendTo.title': 'Send to Workspace',
  'sendTo.selectWorkspace': 'Select workspace',
  'sendTo.description': 'Send {count} {label} to a remote workspace.',
  'sendTo.noRemoteWorkspaces': 'No remote workspaces available.',
  'sendTo.sending': 'Sending...',
  'sendTo.send': 'Send',

  // ── Transport / connection ──────────────────────────────────────
  'transport.disconnected': 'Disconnected',
  'transport.reconnecting': 'Reconnecting...',
  'transport.connectionLost': 'Connection lost. Reconnecting...',
  'transport.connectingToRemote': 'Connecting to remote server',
  'transport.connectingTo': 'Connecting to {url}...',
  'transport.reconnectingToRemote': 'Reconnecting to remote server',
  'transport.cannotConnect': 'Cannot connect to remote server',
  'transport.connectionLostToRemote': 'Connection to remote server lost',
  'transport.remoteServerStatus': 'Remote server connection status',
  'transport.authFailed': 'Authentication failed. Verify CRAFT_SERVER_TOKEN.',
  'transport.protocolMismatch': 'Protocol mismatch between client and server versions.',
  'transport.timeout': 'Connection to {url} timed out. Server may be unreachable.',
  'transport.networkError': 'Could not connect to {url}. Is the remote server running?',
  'transport.wsClosed': 'WebSocket closed with code {code}{reason}.',
  'transport.waitingForConnection': 'Waiting for remote server connection.',

  // ── File viewer ─────────────────────────────────────────────────
  'fileViewer.noPreview': 'No preview available',
  'fileViewer.openFile': 'Open file',
  'fileViewer.noFileSelected': 'No file selected',
  'fileViewer.clickToView': 'Click a file path in the chat to view it here',
  'fileViewer.loading': 'Loading content...',
  'fileViewer.errorLoading': 'Error loading file',

  // ── Workspace choice descriptions ────────────────────────────────
  'workspace.addWorkspaceTagline': 'Where your ideas meet the tools to make them happen.',
  'workspace.createNewDesc': 'Start fresh with an empty workspace.',
  'workspace.openFolderDesc': 'Choose an existing folder as workspace.',
  'workspace.connectRemoteDesc': 'Use a remote OKClaw Pro Server.',

  // ── Annotation ──────────────────────────────────────────────────
  'annotation.addNote': 'Add note',
  'annotation.reply': 'Reply',

  // ── Splash screen ──────────────────────────────────────────────
  'splash.loading': 'Loading...',
  'splash.startingUp': 'Starting up...',

  // ── IslandFollowUp extras ──────────────────────────────────────
  'island.followUp': 'Follow up',
  'island.addComments': 'Add comments the agent should consider in the next turn...',
  'island.saveAndSend': 'Save & Send',
  'island.moreActions': 'More submit actions',

  // ── Status names (default) ─────────────────────────────────────
  'status.backlog': 'Backlog',
  'status.todo': 'Todo',
  'status.inProgress': 'In Progress',
  'status.needsReview': 'Needs Review',
  'status.done': 'Done',
  'status.cancelled': 'Cancelled',

  // ── Rename dialog ───────────────────────────────────────────────
  'renameDialog.placeholder': 'Enter a name...',

  // ── Source selector ────────────────────────────────────────────
  'sourceSelector.filterPlaceholder': 'Search sources...',
  'sourceSelector.emptyLine1': 'No sources configured.',
  'sourceSelector.emptyLine2': 'Add sources in Settings.',
  'sourceSelector.noResults': 'No matching sources.',

  // ── Automation card ────────────────────────────────────────────
  'automations.matching': 'matching',
  'automations.at': 'at',
  'automations.runTest': 'Run Test',
  'automations.runningTest': 'Running test...',
  'automations.testPassed': 'Test Passed',
  'automations.testFailed': 'Test Failed',
  'automations.emptyPrompt': 'Empty prompt',

  // ── Session info popover ───────────────────────────────────────
  'sessionInfo.title': 'Title',
  'sessionInfo.untitled': 'Untitled',
  'sessionInfo.info': 'Folder',

  // ── Session files section ──────────────────────────────────────
  'sessionFiles.header': 'Session Files',
  'sessionFiles.sidebarLabel': 'Session files sidebar',
  'sessionFiles.viewIn': 'View in {fileManager}',
  'sessionFiles.emptyState': 'Files attached or created by this chat will appear here.',
  'sessionFiles.showIn': 'Show in {fileManager}',
  'sessionFiles.directory': 'Directory',
  'sessionFiles.skippedDirectory': 'Skipped large dependency directory',
  'sessionFiles.skippedBadge': 'Skipped',
  'sessionFiles.largeTreeNotice': '{count} items. Large folders are kept collapsed for performance.',

  // ── Table of contents ──────────────────────────────────────────
  'toc.noHeadings': 'No headings',

  // ── Browser empty state ────────────────────────────────────────
  'browser.safetyHint': 'OKClaw Pro only controls browser windows when you ask it to.',

  // ── SetupAuth banner ────────────────────────────────────────────
  'setupAuth.connectionRequired': 'Connection required',
  'setupAuth.apiCredentialsRequired': 'API credentials required',
  'setupAuth.somethingWentWrong': 'Something went wrong',
  'setupAuth.connectServices': 'Connect to required services to continue.',
  'setupAuth.enterCredentials': 'Enter API credentials to continue.',
  'setupAuth.tapToRetry': 'Something went wrong. Tap to retry.',
  'setupAuth.connect': 'Connect',
  'setupAuth.addCredentials': 'Add Credentials',

  // ── Server directory browser ───────────────────────────────────
  'dirBrowser.selectServerDirectory': 'Select Server Directory',
  'dirBrowser.enterPath': 'Enter path...',
  'dirBrowser.go': 'Go',
  'dirBrowser.select': 'Select',
  'dirBrowser.loading': 'Loading...',
  'dirBrowser.showingFirstN': 'Showing the first {count} folders out of {total}. Narrow the path if the folder you want is missing.',
  'dirBrowser.noSubdirectories': 'No subdirectories. Use the path input above to navigate.',
  'dirBrowser.enterFullPath': 'Enter the full path on the server:',
  'dirBrowser.wrongPlatformPath': 'This looks like a path from a different OS. Enter a path that exists on the server.',
  'dirBrowser.failedToList': 'Failed to list directory',
  'dirBrowser.symlink': 'symlink',
  'fileBrowser.selectServerFiles': 'Select files from OKClaw client computer',
  'fileBrowser.select': 'Select',
  'fileBrowser.selectOne': 'Select 1 file',
  'fileBrowser.selectN': 'Select {count} files',
  'fileBrowser.failedToList': 'Failed to list files',
  'fileBrowser.showingFirstN': 'Showing the first {count} items out of {total}. Narrow the path if the file you want is missing.',
  'fileBrowser.noFiles': 'No files in this folder.',

  // ── Skill menu ─────────────────────────────────────────────────
  'skillMenu.openInNewWindow': 'Open in New Window',
  'skillMenu.cloneToWorkspace': 'Clone to Workspace',
  'skillMenu.deleteSkill': 'Delete Skill',

  // ── Empty state hint ───────────────────────────────────────────
  'emptyState.hint0': 'Summarize your {source:Gmail} inbox, draft replies, and save notes to {source:Craft}',
  'emptyState.hint1': 'Turn a {file:screenshot} into a working website in your {folder}',
  'emptyState.hint2': 'Pull issues from {source:Linear}, research in {source:Slack}, ship the fix',
  'emptyState.hint3': 'Transcribe a {file:voice memo} and turn it into {source:Notion} tasks',
  'emptyState.hint4': 'Analyze a {file:spreadsheet} and post insights to {source:Slack}',
  'emptyState.hint5': 'Review {source:GitHub} PRs, then summarize changes in {source:Craft}',
  'emptyState.hint6': 'Parse an {file:invoice PDF} and log it to {source:Google Sheets}',
  'emptyState.hint7': 'Research with {source:Exa}, write it up, save to your {source:Obsidian} vault',
  'emptyState.hint8': 'Refactor code in your {folder}, then push to {source:GitHub}',
  'emptyState.hint9': 'Sync {source:Calendar} events with {source:Linear} project deadlines',
  'emptyState.hint10': 'Turn meeting {file:notes} into {source:Jira} tickets automatically',
  'emptyState.hint11': 'Query your {source:database} and visualize results in a new {file:document}',
  'emptyState.hint12': 'Fetch {source:Figma} designs and generate React components in your {folder}',
  'emptyState.hint13': 'Combine {source:Slack} threads into a weekly digest for {source:Notion}',
  'emptyState.hint14': 'Run a {skill} to analyze your codebase and fix issues in your {folder}',

  // ── Auth ──────────────────────────────────────────────────────────
  'auth.login': 'Login',
  'auth.loginSubtitle': 'Sign in to your OKClaw Pro account',
  'auth.register': 'Register',
  'auth.registerSubtitle': 'Create a new OKClaw Pro account',
  'auth.forgotPassword': 'Forgot password?',
  'auth.resetPassword': 'Reset Password',
  'auth.resetPasswordSubtitle': 'Enter your phone number to reset your password',

  'auth.phone': 'Phone Number',
  'auth.phonePlaceholder': 'Enter your phone number',
  'auth.password': 'Password',
  'auth.passwordPlaceholder': 'Enter your password',
  'auth.smsCode': 'SMS Code',
  'auth.smsCodePlaceholder': 'Enter 6-digit code',
  'auth.captcha': 'Captcha',
  'auth.captchaPlaceholder': 'Enter captcha',
  'auth.newPassword': 'New Password',
  'auth.newPasswordPlaceholder': '6-20 characters',
  'auth.confirmPassword': 'Confirm Password',
  'auth.confirmPasswordPlaceholder': 'Re-enter your new password',

  'auth.sendCode': 'Send Code',
  'auth.sendCodeIn': '{seconds}s',
  'auth.loginWithPassword': 'Password',
  'auth.loginWithSms': 'SMS Code',
  'auth.loginWithWechat': 'WeChat Login',
  'auth.loggingIn': 'Logging in...',
  'auth.registering': 'Registering...',
  'auth.resetting': 'Resetting...',

  'auth.noAccount': "Don't have an account?",
  'auth.hasAccount': 'Already have an account?',
  'auth.backToLogin': 'Back to Login',
  'auth.or': 'or',

  'auth.scanWithWechat': 'Scan with WeChat to login',
  'auth.waitingForScan': 'Open WeChat and scan the QR code',
  'auth.waitingForConfirm': 'Please confirm on your phone',
  'auth.scanned': 'Scanned',
  'auth.scanExpired': 'QR code expired',
  'auth.loadingQr': 'Loading QR code...',
  'auth.qrLoadFailed': 'Failed to load QR code',
  'auth.refresh': 'Refresh',
  'auth.retry': 'Retry',

  'auth.referralCode': 'Referral Code',
  'auth.referralPlaceholder': 'Enter referral code',
  'auth.optional': '(optional)',
  'auth.refreshCaptcha': 'Refresh captcha',

  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.strength': 'Strength',
  'auth.strengthWeak': 'Weak',
  'auth.strengthMedium': 'Medium',
  'auth.strengthStrong': 'Strong',

  'auth.phoneRequired': 'Phone number is required',
  'auth.invalidPhone': 'Please enter a valid 11-digit phone number starting with 1',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordTooShort': 'Password must be at least 6 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.smsCodeRequired': 'SMS code is required',
  'auth.captchaRequired': 'Captcha is required',

  'auth.loginFailed': 'Login failed. Please check your credentials.',
  'auth.registerFailed': 'Registration failed. Please try again.',
  'auth.resetFailed': 'Password reset failed. Please try again.',
  'auth.smsFailed': 'Failed to send SMS code',
  'auth.networkError': 'Network error. Please check your connection.',
  'auth.registerSuccess': 'Registration successful!',
  'auth.resetSuccess': 'Password reset successful! Please login.',

  // ── WeixinV2 Settings ─────────────────────────────────────────
  'weixinV2.status.disabled': 'Disabled',
  'weixinV2.status.awaitingScan': 'Awaiting QR scan',
  'weixinV2.status.running': 'Running ({running}/{total})',
  'weixinV2.status.connecting': 'Connecting...',
  'weixinV2.status.error': 'Error',
  'weixinV2.status.waiting': 'Waiting to connect',
  'weixinV2.accountStatus.running': 'Online',
  'weixinV2.accountStatus.connecting': 'Connecting',
  'weixinV2.accountStatus.error': 'Error',
  'weixinV2.accountStatus.stopped': 'Stopped',
  'weixinV2.defaultAccountLabel': 'WeChat Account',
  'weixinV2.toast.refreshBindingsFailed': 'Failed to refresh bindings',
  'weixinV2.toast.bindingUpdated': 'Binding updated',
  'weixinV2.toast.updateBindingFailed': 'Failed to update binding',
  'weixinV2.toast.bindingRemoved': 'Binding removed',
  'weixinV2.toast.removeBindingFailed': 'Failed to remove binding',
  'weixinV2.bindings.title': 'Conversation Bindings',
  'weixinV2.bindings.description': 'View which workspace and session each WeChat contact is mapped to.',
  'weixinV2.bindings.empty': 'No bindings yet. They will appear automatically once you scan to login and receive messages from contacts.',
  'weixinV2.bindings.currentAccount': '{label} · Current WeChat account',
  'weixinV2.bindings.belongsToAccount': 'Account: {accountId}',
  'weixinV2.bindings.workspace': 'Workspace',
  'weixinV2.bindings.session': 'Session',
  'weixinV2.bindings.currentSession': 'Current session',
  'weixinV2.runStatus.title': 'Running Status',
  'weixinV2.runStatus.description': 'The new WeChat integration uses an independent polling channel and does not affect the legacy integration.',
  'weixinV2.runStatus.currentStatus': 'Current Status',
  'weixinV2.config.title': 'Default Configuration',
  'weixinV2.config.description': 'New contact messages will automatically create sessions using the default workspace and notification policy.',
  'weixinV2.config.notifyMode': 'Default Notification Mode',
  'weixinV2.config.notifyModeDescription': 'In smart mode, desktop replies are pushed to WeChat only when you are not viewing the session.',
  'weixinV2.config.defaultWorkspace': 'Default Workspace',
  'weixinV2.config.defaultWorkspaceDescription': 'When a new contact sends a message for the first time, a session is automatically created in this workspace.',
  'weixinV2.config.outboundAttachmentMode': 'File sending',
  'weixinV2.config.outboundAttachmentModeDescription': 'Local files are listed first and sent only after WeChat confirmation to avoid accidental desktop or workspace file sharing.',
  'weixinV2.attachmentMode.confirm': 'Confirm first',
  'weixinV2.attachmentMode.off': 'Do not send',
  'weixinV2.config.replyProgressMessages': 'Send progress messages',
  'weixinV2.config.replyProgressMessagesDescription': 'Reply with progress updates while long-running tasks are still working.',
  'weixinV2.config.sendAudioAsVoice': 'Send audio as voice messages',
  'weixinV2.config.sendAudioAsVoiceDescription': 'When AI returns an audio file, send supported formats as WeChat voice messages instead of generic files.',
  'weixinV2.config.enableQuotedReply': 'Quote original message',
  'weixinV2.config.enableQuotedReplyDescription': 'Experimental. The protocol has ref_msg, but the official WeChat plugin 2.1.8 does not actually use it on outbound sends; this app can try to send it, but rendering still depends on the WeChat client.',
  'weixinV2.config.enableStreaming': 'Streaming output',
  'weixinV2.config.enableStreamingDescription': 'Experimental. The protocol defines message_state=GENERATING, but the official WeChat plugin 2.1.8 defaults to typing-only updates; this app now sends streaming bubbles when enabled, though desktop clients may still collapse them into preview cards.',
  'weixinV2.config.selectWorkspace': 'Select workspace',
  'weixinV2.notifyMode.auto': 'Smart',
  'weixinV2.notifyMode.always': 'Always',
  'weixinV2.notifyMode.off': 'Off',
  'weixinV2.toast.bridgeEnabled': 'WeChat connection enabled',
  'weixinV2.toast.bridgeStopped': 'WeChat connection stopped',
  'weixinV2.toast.toggleFailed': 'Failed to toggle connection',
  'weixinV2.toast.configSaved': 'Configuration saved',
  'weixinV2.toast.configSaveFailed': 'Failed to save configuration',
  'weixinV2.toast.enableFirst': 'Please enable the connection first',
  'weixinV2.toast.qrFailed': 'Failed to generate QR code',
  'weixinV2.toast.loginFailed': 'WeChat login failed',
  'weixinV2.toast.loginSuccess': 'WeChat account connected',
  'weixinV2.toast.awaitLoginFailed': 'Failed while waiting for scan confirmation',
  'weixinV2.toast.accountRemoved': 'WeChat account removed',
  'weixinV2.toast.removeAccountFailed': 'Failed to remove account',
  'weixinV2.toast.remarkSaved': 'Remark saved',
  'weixinV2.toast.remarkCleared': 'Remark cleared',
  'weixinV2.toast.remarkSaveFailed': 'Failed to save remark',
  'weixinV2.qrLogin.title': 'QR Login',
  'weixinV2.qrLogin.description': 'Each scan connects a new WeChat account. Multiple accounts can run simultaneously.',
  'weixinV2.qrLogin.scanPrompt': 'Please scan the QR code with WeChat and confirm login on your phone.',
  'weixinV2.qrLogin.awaitingConfirm': 'Awaiting phone confirmation...',
  'weixinV2.qrLogin.generatePrompt': 'Generate a QR code to connect your WeChat account to OKClaw.',
  'weixinV2.qrLogin.generateButton': 'Generate Login QR Code',
  'weixinV2.qrLogin.enableFirst': 'Please enable the connection first',
  'weixinV2.accounts.title': 'Connected Accounts',
  'weixinV2.accounts.description': 'View connected WeChat accounts and their running status.',
  'weixinV2.accounts.empty': 'No connected WeChat accounts.',
  'weixinV2.accounts.localRemark': 'Local Remark',
  'weixinV2.accounts.accountId': 'Account ID: {id}',
  'weixinV2.accounts.userId': 'User: {id}',
  'weixinV2.accounts.statusLabel': 'Status: {status}',
  'weixinV2.accounts.lastInbound': 'Last inbound: {time}',
  'weixinV2.accounts.lastOutbound': 'Last outbound: {time}',
  'weixinV2.accounts.noActivity': 'None',
  'weixinV2.accounts.remarkName': 'Local Remark Name',
  'weixinV2.accounts.remarkPlaceholder': 'e.g., Support A / Sales B',
  'weixinV2.accounts.saveRemark': 'Save Remark',
  'weixinV2.accounts.remarkHint': 'Leave empty to fall back to the system-generated account identifier.',
  'weixinV2.accounts.remove': 'Remove',
  'weixinV2.commands.title': 'Command Reference',
  'weixinV2.commands.description': 'Send these commands directly in WeChat to manage the session for the current contact.',
  'weixinV2.commands.help': 'View help and command list',
  'weixinV2.commands.new': 'Create new session and rebind current contact',
  'weixinV2.commands.list': 'List recent sessions in current workspace',
  'weixinV2.commands.switch': 'Switch to the session with the given number',
  'weixinV2.commands.workspace': 'View or switch workspace',
  'weixinV2.commands.stop': 'Stop the current Agent task',
  'weixinV2.commands.now': 'View current status',
  'weixinV2.dialog.removeAccount.title': 'Remove WeChat Account',
  'weixinV2.dialog.removeAccount.description': 'This will stop polling for this account and clear its contact binding records.',
  'weixinV2.tabs.accounts': 'Accounts & Config',
  'weixinV2.tabs.bindings': 'Binding Management',

  // ── Cron Builder ──────────────────────────────────────────────
  'cron.field.minute': 'Minute',
  'cron.field.hour': 'Hour',
  'cron.field.day': 'Day',
  'cron.field.month': 'Month',
  'cron.field.weekday': 'Weekday',
  'cron.month.1': 'Jan',
  'cron.month.2': 'Feb',
  'cron.month.3': 'Mar',
  'cron.month.4': 'Apr',
  'cron.month.5': 'May',
  'cron.month.6': 'Jun',
  'cron.month.7': 'Jul',
  'cron.month.8': 'Aug',
  'cron.month.9': 'Sep',
  'cron.month.10': 'Oct',
  'cron.month.11': 'Nov',
  'cron.month.12': 'Dec',
  'cron.weekday.0': 'Sun',
  'cron.weekday.1': 'Mon',
  'cron.weekday.2': 'Tue',
  'cron.weekday.3': 'Wed',
  'cron.weekday.4': 'Thu',
  'cron.weekday.5': 'Fri',
  'cron.weekday.6': 'Sat',
  'cron.commonSchedules': 'Common Schedules',
  'cron.customSchedule': 'Custom Schedule',
  'cron.advanced': 'Advanced',
  'cron.validation.fiveParts': 'Schedule needs 5 parts: minute, hour, day, month, and weekday',
  'cron.validation.invalidField': 'Invalid value in {field}: "{value}"',
  'cron.nextRuns': 'Next runs:',
  'cron.timezone': 'Timezone:',
  'cron.systemDefault': 'System default',

  // ── Browser empty state ────────────────────────────────────────
  'browser.emptyState.title': 'This browser is ready for your Agents - and you ;)',
  'browser.emptyState.description': 'Ask any session to use this browser (or open another one) to complete tasks like research, form filling, QA checks, or data extraction.',

  // ── Subscription widget ────────────────────────────────────────
  'subscription.refreshQuota': 'Refresh quota',

  // ── Account profile ───────────────────────────────────────────
  'account.profile': 'Account Profile',
  'account.profile.description': 'View profile, balance, and security information.',
  'account.refresh': 'Refresh',
  'account.balance': 'Available Balance',
  'account.info': 'Profile Information',
  'account.username': 'Username',
  'account.studentCertification': 'Student Certification',
  'account.phone': 'Phone',
  'account.wechat': 'WeChat',
  'account.email': 'Email',
  'account.password': 'Password',
  'account.session': 'Login Status',
  'account.logout': 'Log Out',
  'account.logoutDescription': 'Log out of your OKClaw Pro account and return to the login page.',
  'account.loggingOut': 'Logging out...',
  'account.action.recharge': 'Recharge',
  'account.action.modify': 'Modify',
  'account.action.view': 'View',
  'account.action.bind': 'Bind',
  'account.action.rebind': 'Rebind',
  'account.action.set': 'Set',
  'account.action.applyCertification': 'Apply',
  'account.action.resubmitCertification': 'Resubmit',
  'account.actionUnavailable': 'Please complete this operation in the web account center.',

  // ── Model search ───────────────────────────────────────────────
  'model.searchPlaceholder': 'Search models...',
  'model.noResults': 'No models found',

  // ── Session filter ─────────────────────────────────────────────
  'session.searchStatusesLabels': 'Search statuses & labels...',
  'sessionFilter.statuses': 'Statuses',
  'sessionFilter.labels': 'Labels',
  'sessionFilter.group': 'Group',
  'sessionFilter.groupByDate': 'Date',
  'sessionFilter.groupByStatus': 'Status',
  'sessionFilter.noLabelsConfigured': 'No labels configured',
  'sessionFilter.noMatchingStatusesLabels': 'No matching statuses or labels',

  // ── Common UI ──────────────────────────────────────────────────
  'common.edit': 'Edit',
  'common.noResults': 'No results found',
  'common.failedToSave': 'Failed to save',
  'common.editFile': 'Edit File',
  'common.never': 'Never',
  'common.justNow': 'Just now',
  'common.minutesAgo': '{count} minute(s) ago',
  'common.hoursAgo': '{count} hour(s) ago',
  'common.daysAgo': '{count} day(s) ago',
  'common.revert': 'Revert',

  // ── Appearance settings ────────────────────────────────────────
  'settings.appearance.themeWarning': 'Theme warning: {error} ({fallback})',
  'settings.appearance.themeWarningBundled': 'using bundled fallback',
  'settings.appearance.themeWarningDefault': 'using default theme',

  // ── Chat page ──────────────────────────────────────────────────
  'chat.fileMatchNotification': 'Opened closest match: {path}',
  'chat.linkCopied': 'Link copied to clipboard',
  'chat.shareError': 'Failed to share',
  'chat.shareUpdated': 'Share updated',
  'chat.updateShareError': 'Failed to update share',
  'chat.sharingStopped': 'Sharing stopped',
  'chat.stopSharingError': 'Failed to stop sharing',
  'chat.openInBrowser': 'Open in Browser',
  'chat.copyLink': 'Copy Link',
  'chat.updateShare': 'Update Share',
  'chat.stopSharing': 'Stop Sharing',
  'chat.learnMore': 'Learn More',
  'chat.shareOnline': 'Share Online',
  'chat.sessionNotFound': 'This session no longer exists',

  // ── Preferences page ──────────────────────────────────────────
  'preferences.title': 'Preferences',
  'preferences.basicInfo': 'Basic Info',
  'preferences.name': 'Name',
  'preferences.namePlaceholder': 'Your name',
  'preferences.timezone': 'Timezone',
  'preferences.timezonePlaceholder': 'e.g., America/New_York',
  'preferences.language': 'Language',
  'preferences.languagePlaceholder': 'e.g., English',
  'preferences.location': 'Location',
  'preferences.city': 'City',
  'preferences.cityPlaceholder': 'e.g., New York',
  'preferences.country': 'Country',
  'preferences.countryPlaceholder': 'e.g., USA',
  'preferences.notes': 'Notes',
  'preferences.notesPlaceholder': 'Any additional information you\'d like to share with the AI assistant...',

  // ── Source info page ──────────────────────────────────────────
  'source.notFound': 'Source not found',
  'source.loadError': 'Failed to load source',
  'source.deleted': 'Deleted source: {name}',
  'source.deleteError': 'Failed to delete source',
  'source.disabledTitle': 'Source Disabled',
  'source.disabledWarning': 'Local MCP servers are disabled in Settings > Advanced. Enable them to use this source.',
  'source.mcpStdioDescription': 'Local command that spawns this MCP server.',
  'source.mcpNetworkDescription': 'Server URL and connection status.',
  'source.apiDescription': 'Base URL for API requests.',
  'source.localDescription': 'Filesystem path for this source.',
  'source.connectionDefault': 'Connection details.',
  'source.mcpPermissionsDescription': 'Tool patterns allowed in Explore mode.',
  'source.apiPermissionsDescription': 'API endpoints allowed in Explore mode.',
  'source.permissionsDefault': 'Access rules for Explore mode.',
  'source.toolsDescription': 'Operations exposed by this server.',
  'source.documentationDescription': 'Context and guidelines for the agent.',
  'info.lastTested': 'Last Tested',

  // ── Skill info page ───────────────────────────────────────────
  'skill.notFound': 'Skill not found',
  'skill.loadError': 'Failed to load skill',
  'skill.deleted': 'Deleted skill: {name}',
  'skill.deleteError': 'Failed to delete skill',
  'skill.deleteSkill': 'Delete Skill',
  'skill.managedByProject': 'Managed by project',
  'skill.slug': 'Slug',
  'skill.source': 'Source',
  'skill.location': 'Location',
  'skill.requiredSources': 'Required Sources',
  'skill.sourceBundled': 'Bundled (ships with app)',
  'skill.sourceProject': 'Project (.agents/skills/)',
  'skill.sourceGlobal': 'Global (~/.agents/skills/)',
  'skill.sourceWorkspace': 'Workspace',
  'skill.cloneToWorkspace': 'Clone to Workspace',
  'skill.cloneSuccess': 'Cloned "{name}" to workspace',
  'skill.cloneError': 'Failed to clone skill',
  'skill.permissionModesDescription': 'How "Always Allowed Tools" interacts with permission modes:',
  'skill.modeExploreDescription': 'Blocked — write tools blocked regardless',
  'skill.modeAskToEditDescription': 'Auto-approved — no prompts for allowed tools',
  'skill.modeAutoDescription': 'No effect — all tools already auto-approved',
  'skill.noInstructions': '*No instructions provided.*',

  // ── Browser toolbar ───────────────────────────────────────────
  'browser.windowOptions': 'Browser window options',
  'browser.hideWindow': 'Hide Window',
  'browser.closeWindowEntirely': 'Close Window Entirely',

  // ── Task action menu ──────────────────────────────────────────
  'task.terminalNotAvailable': 'Terminal overlay not available',
  'task.loadOutputFailed': 'Failed to load task output',
  'task.viewOutput': 'View Output',
  'task.stopTask': 'Stop Task',
  'task.typeAgent': 'Task',
  'task.typeShell': 'Shell',

  // ── Source/Skill menus ────────────────────────────────────────
  'menu.openInNewWindow': 'Open in New Window',
  'menu.learnMore': 'Learn More',

  // ── Workspace ─────────────────────────────────────────────────
  'workspace.authFailed': 'Authentication failed — click to reconnect',
  'workspace.serverUnreachable': 'Server unreachable — click to reconnect',
  'workspace.disconnected': 'Disconnected — click to reconnect',
  'workspace.cannotRemoveActive': 'Cannot remove the active workspace',
  'workspace.created': 'Created workspace "{name}"',
  'workspace.removed': 'Removed "{name}"',
  'workspace.workspaceName': 'Workspace name',
  'workspace.location': 'Location',
  'workspace.browse': 'Browse',
  'workspace.createWorkspaceDesc': 'Enter a name and choose where to store your workspace.',
  'workspace.chooseAnyFolder': 'Choose any folder to use as workspace.',
  'workspace.noFolderSelected': 'No folder selected',
  'workspace.creating': 'Creating...',
  'workspace.opening': 'Opening...',
  'workspace.reconnectTitle': 'Reconnect "{name}"',
  'workspace.reconnectDesc': 'Update the server URL or token to restore the connection.',
  'workspace.connectRemoteFullDesc': 'Connect to a remote OKClaw Pro Server for this workspace.',
  'workspace.token': 'Token',
  'workspace.testing': 'Testing...',
  'workspace.connected': 'Connected',
  'workspace.connectedNoWorkspaces': 'no workspaces yet',
  'workspace.oldServerWarning': 'This server is running an older version. Some features like remote directory browsing may not work correctly. Update the server for full functionality.',
  'workspace.remoteWorkspace': 'Workspace',
  'workspace.createNewOnServer': 'Create new workspace on server',
  'workspace.remoteWorkspaceHint': 'A workspace will be created on the remote server with this name.',
  'workspace.useExistingWorkspace': 'Use existing workspace',
  'workspace.reconnecting': 'Reconnecting...',
  'workspace.createAndConnect': 'Create and Connect',
  'workspace.connect': 'Connect',
  'workspace.creatingAndConnecting': 'Creating...',
  'workspace.slugExists': 'A workspace named "{slug}" already exists',

  // ── Session status menu ───────────────────────────────────────
  'session.noStatusFound': 'No status found',
  'session.unarchive': 'Unarchive',
  'session.archive': 'Archive',

  // ── Info badges ───────────────────────────────────────────────
  'info.allowed': 'Allowed',
  'info.blocked': 'Blocked',
  'info.ask': 'Ask',
  'info.authenticateToView': 'Authenticate with this source to view available data',
  'info.color': 'Color',

  // ── Automation ────────────────────────────────────────────────
  'automation.phaseScheduled': 'Scheduled',
  'automation.phaseBefore': 'Before',
  'automation.phaseAfter': 'After',
  'automation.phaseOnError': 'On Error',
  'automation.phaseEvent': 'Event',
  'automation.disable': 'Disable',
  'automation.enable': 'Enable',
  'automation.runTest': 'Run Test',
  'automation.duplicate': 'Duplicate',
  'automation.prompt': 'Prompt',
  'automation.webhook': 'Webhook',
  'automation.lastRan': 'Last ran {time}',

  // ── Auth request card ─────────────────────────────────────────
  'auth.bearerToken': 'Bearer Token',
  'auth.apiKey': 'API Key',
  'auth.username': 'Username',
  'auth.typeOAuth': 'OAuth',
  'auth.typeGoogle': 'Google Sign-In',
  'auth.typeSlack': 'Slack Sign-In',
  'auth.typeMicrosoft': 'Microsoft Sign-In',
  'auth.typeDefault': 'Authentication',
  'auth.authenticating': 'Authenticating...',
  'auth.completeInBrowser': 'Complete authentication in your browser',
  'auth.saving': 'Saving...',
  'auth.credentialsEncrypted': 'Credentials are encrypted at rest',

  // ── Browser tabs ──────────────────────────────────────────────
  'browser.newTab': 'New Tab',

  // ── Onboarding: API setup ─────────────────────────────────────
  'onboarding.segmentClaude': 'Claude',
  'onboarding.segmentOKClaw': 'OKClaw Pro Backend',
  'onboarding.beta': 'Beta',
  'onboarding.segmentClaudeDesc': 'Use Claude Agent SDK as the main agent. Configure with your Claude subscription or API key.',
  'onboarding.segmentOKClawDesc': 'Use OKClaw Pro Backend as the main agent. Configure with your API key, OAuth subscription, or custom endpoint.',
  'onboarding.optionClaudeMax': 'Claude Pro/Max',
  'onboarding.optionAnthropicKey': 'Anthropic API Key',
  'onboarding.optionChatGPT': 'ChatGPT Plus',
  'onboarding.optionCopilot': 'GitHub Copilot',
  'onboarding.optionApiKey': 'API Key',

  // ── Onboarding: Credentials ───────────────────────────────────
  'onboarding.chatgptDescription': 'Use your ChatGPT subscription to power OKClaw Pro.',
  'onboarding.signInChatGPT': 'Sign in with ChatGPT',
  'onboarding.chatgptOpenAIPrompt': 'Click the button above to sign in with your OpenAI account. A browser window will open for authentication.',
  'onboarding.chatgptConnected': 'Connected! Your ChatGPT subscription is ready.',
  'onboarding.copilotDescription': 'Use your GitHub Copilot subscription to power OKClaw Pro.',
  'onboarding.waitingAuth': 'Waiting for authorization...',
  'onboarding.signInGitHub': 'Sign in with GitHub',
  'onboarding.enterCodePrompt': 'Enter this code on GitHub to authorize:',
  'onboarding.copiedToClipboard': 'Copied to clipboard',
  'onboarding.browserOpenedGitHub': 'A browser window should have opened to github.com/login/device',
  'onboarding.clickToSignInGitHub': 'Click the button above to sign in with your GitHub account.',
  'onboarding.copilotConnected': 'Connected! Your GitHub Copilot subscription is ready.',
  'onboarding.pasteCodeBelow': 'Copy the code from the browser page and paste it below.',
  'onboarding.claudeDescription': 'Use your Claude subscription to power multi-agent workflows.',
  'onboarding.signInClaude': 'Sign in with Claude',
  'onboarding.piApiKeyDescription': 'Select a provider preset and enter the API key. For arbitrary Anthropic-compatible endpoints, use Anthropic API Key mode.',
  'onboarding.anthropicApiKeyDescription': 'Enter your API key. Optionally configure a custom endpoint for OpenRouter, Ollama, or compatible APIs.',

  // ── Onboarding: Local model ───────────────────────────────────
  'onboarding.localModelDescription': 'Connect to Ollama or any OpenAI-compatible local server.',
  'onboarding.endpointRequired': 'Endpoint URL is required.',
  'onboarding.modelRequired': 'At least one model name is required.',
  'onboarding.endpoint': 'Endpoint',
  'onboarding.ollamaPortHint': 'Default Ollama port is 11434. Change if using a custom setup.',
  'onboarding.model': 'Model',
  'onboarding.required': '· required',
  'onboarding.ollamaModelHint': 'Use any model pulled via <code>ollama pull</code>. Comma-separated for multiple models.',

  // ── Onboarding: Provider select ───────────────────────────────
  'onboarding.howToConnect': 'How would you like to connect?',
  'onboarding.okclawManagedModelsDescription': 'OKClaw Pro manages model access through your OKClaw account. Sign in or refresh your subscription to update available models.',
  'onboarding.setupLater': 'Setup later',

  // ── OAuth ─────────────────────────────────────────────────────
  'auth.authorizationCode': 'Authorization Code',

  // ── Reauth screen ─────────────────────────────────────────────
  'onboarding.sessionExpiredDesc': 'Your Craft session has expired or is no longer valid.',
  'onboarding.pleaseLoginAgain': 'Please log in again to continue using OKClaw Pro.',
  'onboarding.settingsPreserved': 'Your conversations and settings are preserved.',
  'onboarding.loggingIn': 'Logging in...',
  'onboarding.logInWithCraft': 'Log In with Craft',
  'onboarding.resetApp': 'Reset app and start fresh...',

  // ── Git Bash warning ──────────────────────────────────────────
  'onboarding.gitBashDescription': 'OKClaw Pro needs Git Bash to run shell commands on Windows. It was not found on your system.',
  'onboarding.installGitWindows': 'Install Git for Windows',
  'onboarding.installGitDesc': 'The easiest way to get Git Bash. It\'s free and includes everything you need.',
  'onboarding.downloadGitWindows': 'Download Git for Windows',
  'onboarding.autoInstallGitWindows': 'Automatically install Git Bash',
  'onboarding.autoInstallGitDesc': 'OKClaw Pro will download Git for Windows and run the installer before continuing to startup.',
  'onboarding.installingGitWindows': 'Installing Git Bash...',
  'onboarding.retryInstallGitWindows': 'Retry automatic install',
  'onboarding.gitBashInstallFailed': 'Automatic Git Bash installation failed. You can retry or choose bash.exe manually.',
  'onboarding.alreadyHaveGit': 'Already have Git installed?',
  'onboarding.nonStandardPath': 'If Git is installed in a non-standard location, you can specify the path to bash.exe.',
  'onboarding.useThisPath': 'Use this path',
  'onboarding.recheck': 'Re-check',
  'onboarding.browse': 'Browse...',

  // ── Source list ───────────────────────────────────────────────
  'source.typeLocalFolder': 'local folder',

  // ── Automation empty states ────────────────────────────────────
  'automations.emptyDescription': 'Automations run actions when events occur — execute commands on schedules, react to label changes, or trigger prompts automatically.',
  'automations.addAutomation': 'Add Automation',
  'automations.noAutomationsFound': 'No automations found',
  'automations.noAutomationsConfigured': 'No automations configured',
  'automations.clearSearch': 'Clear search',
  'automations.schedulingRequirement': 'Scheduling requires your machine to be running. It can be locked, but must be powered on.',

// Automation builder
  'automations.builder.title': 'Automation Builder',
  'automations.builder.createTitle': 'Create Automation',
  'automations.builder.editTitle': 'Edit Automation',
  'automations.builder.loading': 'Loading automation builder...',
  'automations.builder.back': 'Back',
  'automations.builder.saveDraft': 'Save Draft',
  'automations.builder.savingDraft': 'Saving Draft...',
  'automations.builder.saveAndEnable': 'Save & Enable',
  'automations.builder.savingEnable': 'Saving...',
  'automations.builder.name': 'Name',
  'automations.builder.namePlaceholder': 'Automation name',
  'automations.builder.event': 'Event',
  'automations.builder.schedule.mode': 'Schedule Mode',
  'automations.builder.schedule.mode.preset': 'Common Presets',
  'automations.builder.schedule.mode.rule': 'Build by Rule',
  'automations.builder.schedule.mode.custom': 'Custom Cron',
  'automations.builder.schedule.preset': 'Common Schedule',
  'automations.builder.schedule.presetPlaceholder': 'Select a schedule',
  'automations.builder.schedule.ruleType': 'Rule Type',
  'automations.builder.schedule.rule.everyMinutes': 'Every N Minutes',
  'automations.builder.schedule.rule.everyHours': 'Every N Hours',
  'automations.builder.schedule.rule.daily': 'Every Day',
  'automations.builder.schedule.rule.weekdays': 'Workdays',
  'automations.builder.schedule.rule.weekly': 'Every Week',
  'automations.builder.schedule.rule.monthly': 'Every Month',
  'automations.builder.schedule.intervalMinutes': 'Minutes Interval',
  'automations.builder.schedule.intervalHours': 'Hours Interval',
  'automations.builder.schedule.minuteOfHour': 'Minute',
  'automations.builder.schedule.time': 'Time',
  'automations.builder.schedule.weekdays': 'Weekdays',
  'automations.builder.schedule.dayOfMonth': 'Day of Month',
  'automations.builder.schedule.generatedCron': 'Generated Cron',
  'automations.builder.schedule.group.frequent': 'Frequent',
  'automations.builder.schedule.group.daily': 'Daily',
  'automations.builder.schedule.group.workdays': 'Workdays',
  'automations.builder.schedule.group.weekly': 'Weekly',
  'automations.builder.schedule.group.monthly': 'Monthly',
  'automations.builder.schedule.option.everyMinutes': 'Every {count} minutes',
  'automations.builder.schedule.option.everyHours': 'Every {count} hours',
  'automations.builder.schedule.option.dailyAt': 'Every day {time}',
  'automations.builder.schedule.option.weekdaysAt': 'Workdays {time}',
  'automations.builder.schedule.option.weeklyAt': 'Every week {days} {time}',
  'automations.builder.schedule.option.monthlyAt': 'Every month day {day} {time}',
  'automations.builder.schedule.weekday.sunday': 'Sun',
  'automations.builder.schedule.weekday.monday': 'Mon',
  'automations.builder.schedule.weekday.tuesday': 'Tue',
  'automations.builder.schedule.weekday.wednesday': 'Wed',
  'automations.builder.schedule.weekday.thursday': 'Thu',
  'automations.builder.schedule.weekday.friday': 'Fri',
  'automations.builder.schedule.weekday.saturday': 'Sat',
  'automations.builder.matcher': 'Matcher',
  'automations.builder.matcherPlaceholder': 'Regex or match text',
  'automations.builder.permissionMode': 'Permission Mode',
  'automations.builder.defaultPermissionMode': 'Default',
  'automations.builder.cron': 'Cron',
  'automations.builder.timezone': 'Timezone',
  'automations.builder.timezone.group.current': 'Current Value',
  'automations.builder.timezone.group.africa': 'Africa',
  'automations.builder.timezone.group.america': 'America',
  'automations.builder.timezone.group.antarctica': 'Antarctica',
  'automations.builder.timezone.group.arctic': 'Arctic',
  'automations.builder.timezone.group.asia': 'Asia',
  'automations.builder.timezone.group.atlantic': 'Atlantic',
  'automations.builder.timezone.group.australia': 'Australia',
  'automations.builder.timezone.group.europe': 'Europe',
  'automations.builder.timezone.group.indian': 'Indian',
  'automations.builder.timezone.group.pacific': 'Pacific',
  'automations.builder.timezone.group.other': 'Other',
  'automations.builder.timezone.searchPlaceholder': 'Search timezone',
  'automations.builder.timezone.empty': 'No matching timezones',
  'automations.builder.addPrompt': 'Add Prompt',
  'automations.builder.addWebhook': 'Add Webhook',
  'automations.builder.addChannel': 'Add Channel',
  'automations.builder.addNotification': 'Add Notification',
  'automations.builder.noActions': 'No actions yet. Add a prompt or notification action.',
  'automations.builder.action': 'Action',
  'automations.builder.remove': 'Remove',
  'automations.builder.promptPlaceholder': 'What should the automation ask the agent to do?',
  'automations.builder.method': 'Method',
  'automations.builder.bodyFormat': 'Body Format',
  'automations.builder.subjectPlaceholder': 'Mail subject',
  'automations.builder.legacyWebhookNotice': 'This automation contains a legacy webhook action. Builder editing is no longer available here, but you can keep or remove it.',
  'automations.builder.conditionsDescription': 'Structured condition editing will be expanded next. Existing conditions are preserved on save.',
  'automations.builder.reviewDescription': 'Save incomplete automations as drafts, and only enable them when all channel blockers are cleared.',
  'automations.builder.blockers': 'Blockers',
  'automations.builder.channelSetup.loading': 'Checking channel setup...',
  'automations.builder.channelSetup.availableTargets': 'Available targets',
  'automations.builder.channelSetup.selectTarget': 'Select a target',
  'automations.builder.channelSetup.selectedTarget': 'Selected target',
  'automations.builder.channelSetup.recipient': 'Recipient',
  'automations.builder.channelSetup.channelTargetMemoryHint': 'This notification remembers a separate target for each channel while you edit.',
  'automations.builder.channelSetup.autoSelectedTargetHint': 'The only available target was selected automatically.',
  'automations.builder.channelSetup.invalidRememberedTargetHint': 'The previously remembered target is no longer available for this channel.',
  'automations.builder.channelSetup.openToolSettings': 'Open Tool Settings',
  'automations.builder.channelSetup.refreshStatus': 'Refresh Status',
  'automations.builder.channelSetup.failedToLoad': 'Failed to load channel setup state',
  'automations.builder.publish.trigger': 'Trigger',
  'automations.builder.publish.conditions': 'Conditions',
  'automations.builder.publish.publish': 'Publish',
  'automations.builder.publish.cronMissing': 'Cron missing',
  'automations.builder.publish.noMatcherConstraint': 'No matcher constraint',
  'automations.builder.publish.noExtraConditions': 'No extra conditions',
  'automations.builder.publish.conditionsCount': '{count} condition(s)',
  'automations.builder.publish.readyToEnable': 'Ready to enable',
  'automations.builder.publish.draftOnly': 'Draft only',
  'automations.builder.publish.needsFixes': 'Needs fixes',
  'automations.builder.step.trigger': 'Trigger',
  'automations.builder.step.conditions': 'Conditions',
  'automations.builder.step.actions': 'Actions',
  'automations.builder.step.channel-setup': 'Channel Setup',
  'automations.builder.step.review': 'Review & Publish',
  'automations.builder.actionType.prompt': 'Prompt',
  'automations.builder.actionType.webhook': 'Webhook',
  'automations.builder.actionType.channel': 'Notification',
  'automations.builder.review.emptyPrompt': 'Empty prompt',
  'automations.builder.review.urlMissing': '{method} URL missing',
  'automations.builder.review.targetNotSelected': '{channel} target not selected',
  'automations.builder.readiness.unconfigured.label': 'Not configured',
  'automations.builder.readiness.unconfigured.message': '{channel} is not configured yet',
  'automations.builder.readiness.configured.label': 'Configured',
  'automations.builder.readiness.configured.message': '{channel} is configured, but not connected yet',
  'automations.builder.readiness.test_failed.label': 'Test failed',
  'automations.builder.readiness.test_failed.message': '{channel} connection test failed',
  'automations.builder.readiness.ready_no_target.label': 'Target required',
  'automations.builder.readiness.ready_no_target.message': '{channel} is ready, but a target still needs to be selected',
  'automations.builder.readiness.ready.label': 'Ready',
  'automations.builder.readiness.ready.message': '{channel} is ready',
  'automations.builder.readiness.degraded.label': 'Needs repair',
  'automations.builder.readiness.degraded.message': 'The selected {channel} target is no longer available',
  'automations.builder.issue.actionRequired': 'At least one action is required',
  'automations.builder.issue.cronRequired': 'Cron is required for scheduled automations',
  'automations.builder.issue.promptEmpty': 'Prompt cannot be empty',
  'automations.builder.issue.webhookUrlEmpty': 'Webhook URL cannot be empty',
  'automations.builder.issue.mailRecipientRequired': 'Mail recipient is required',
  'automations.builder.issue.mailRecipientInvalid': 'Mail recipient is invalid',
  'automations.builder.issue.targetMissing': '{channel} target missing',
  'automations.builder.issue.targetInvalid': '{channel} target invalid',
  'automations.builder.issue.testFailed': '{channel} test failed',
  'automations.builder.issue.notConnected': '{channel} not connected yet',
  'automations.builder.issue.notConfigured': '{channel} not configured',
  'automations.builder.issue.notChecked': '{channel} setup not checked',
  'automations.builder.issue.noWorkspace': 'No workspace selected',
  'automations.builder.issue.automationNotFound': 'Automation not found',
  'automations.builder.issue.loadConfigFailed': 'Failed to load automation config',
  'automations.builder.issue.invalidDraft': 'Automation draft is invalid',
  'automations.builder.issue.saveUnsupported': 'The connected workspace server is too old to save automations. Reconnect or upgrade the server and try again.',
  'automations.builder.issue.scheduledOnly': 'The automation builder currently only supports scheduled automations.',
  'automations.builder.toast.savedDraft': 'Automation saved as draft',
  'automations.builder.toast.savedAndEnabled': 'Automation saved and enabled',
  'automations.builder.toast.saveFailed': 'Failed to save automation',
  'automations.builder.event.LabelAdd': 'Label Added',
  'automations.builder.event.LabelRemove': 'Label Removed',
  'automations.builder.event.LabelConfigChange': 'Label Settings Changed',
  'automations.builder.event.PermissionModeChange': 'Permission Changed',
  'automations.builder.event.FlagChange': 'Flag Changed',
  'automations.builder.event.TodoStateChange': 'Task Updated',
  'automations.builder.event.SessionStatusChange': 'Status Changed',
  'automations.builder.event.SchedulerTick': 'Scheduled',
  'automations.builder.event.PreToolUse': 'Before Tool Runs',
  'automations.builder.event.PostToolUse': 'After Tool Runs',
  'automations.builder.event.PostToolUseFailure': 'When Tool Fails',
  'automations.builder.event.Notification': 'Notification',
  'automations.builder.event.UserPromptSubmit': 'Message Sent',
  'automations.builder.event.SessionStart': 'Session Started',
  'automations.builder.event.SessionEnd': 'Session Ended',
  'automations.builder.event.Stop': 'Agent Stopped',
  'automations.builder.event.SubagentStart': 'Sub-agent Started',
  'automations.builder.event.SubagentStop': 'Sub-agent Stopped',
  'automations.builder.event.PreCompact': 'Before Memory Cleanup',
  'automations.builder.event.PermissionRequest': 'Permission Request',
  'automations.builder.event.Setup': 'Setup',

  // ── Channel delivery ─────────────────────────────────────────
  'automations.channelAction': 'Channel Delivery',
  'automations.channelAction.description': 'Deliver automation results to a messaging channel',
  'automations.channelAction.channel': 'Channel',
  'automations.channelAction.target': 'Target',
  'automations.channelAction.targetPlaceholder': 'Chat ID, email address, etc.',
  'automations.channelAction.subject': 'Email Subject',
  'automations.channelAction.format': 'Format',
  'automations.channelAction.formatText': 'Plain Text',
  'automations.channelAction.formatMarkdown': 'Markdown',
  'automations.channelAction.formatHtml': 'HTML',
  'automations.channelAction.delivered': 'Delivered to {channel}',
  'automations.channelAction.deliveryFailed': 'Delivery to {channel} failed: {error}',
  'automations.channelAction.notSupported': 'Channel "{channel}" does not yet support proactive delivery',

  // ── Edit popover ──────────────────────────────────────────────
  'editPopover.whatToChange': 'What would you like to change?',
  'editPopover.justDescribe': 'Just describe it — I\'ll handle the rest',
  'editPopover.placeholder1': 'Just tell me what to change',
  'editPopover.placeholder2': 'Describe the update',
  'editPopover.placeholder3': 'What should I modify?',
  'editPopover.defaultPlaceholder': 'Describe what you\'d like to change...',

  // ── Update checker toasts ─────────────────────────────────────
  'update.available': 'New version v{version} found',
  'update.availableDesc': 'Auto-download has started. You can restart once it finishes.',
  'update.installing': 'Installing update...',
  'update.installingDesc': 'The app will restart automatically.',
  'update.installFailed': 'Failed to install update',
  'update.ready': 'Update v{version} ready',
  'update.restartToApply': 'Restart to apply the update.',
  'update.restart': 'Restart',
  'update.upToDate': 'You\'re up to date',
  'update.upToDateDesc': 'Version {version} is the latest.',
  'update.checkFailed': 'Failed to check for updates',
  'update.downloadFailed': 'Failed to download update',

  // ── App-level toasts ──────────────────────────────────────────
  'toast.vcredistMissing': 'Microsoft Visual C++ Redistributable not found',
  'toast.vcredistMissingDesc': 'Document tools (PDF, PPTX, DOCX, XLSX) require it to work. Restart after installing.',
  'toast.install': 'Install',
  'toast.failedOpenFile': 'Failed to open file',
  'toast.failedOpenLink': 'Failed to open link',
  'toast.failedOpenLinkLocalHint': '{message}. If this is a local path, use Open File instead.',
  'toast.failedRevealIn': 'Failed to reveal in {fileManager}',
  'toast.reconnectFailed': 'Reconnect failed',
  'toast.cannotSendNow': 'Cannot send right now',
  'toast.branchProviderMismatch': 'Branching is only supported within the same provider/backend. Switch this panel connection and try again.',
  'toast.couldNotCreateBranch': 'Could not create branch',
  'toast.couldNotSaveHighlight': 'Could not save highlight',
  'toast.couldNotRemoveHighlight': 'Could not remove highlight',
  'toast.couldNotUpdateHighlight': 'Could not update highlight',
  'toast.failedCreateBrowserWindow': 'Failed to create browser window',
  'toast.deletedSource': 'Deleted source',
  'toast.failedDeleteSource': 'Failed to delete source',
  'toast.deletedSkill': 'Deleted skill: {slug}',
  'toast.failedDeleteSkill': 'Failed to delete skill',
  'toast.importedSkill': 'Skill imported successfully',
  'toast.failedImportSkill': 'Failed to import skill',
  'toast.skillAlreadyExists': 'Skill already exists',
  'toast.clonedSkill': 'Cloned skill to workspace: {slug}',
  'toast.failedCloneSkill': 'Failed to clone skill',
  'toast.invalidLink': 'Invalid link',
  'toast.invalidLinkDesc': 'The content may have been moved or deleted.',
  'toast.failedToggleAutomation': 'Failed to toggle automation',
  'toast.failedDuplicateAutomation': 'Failed to duplicate automation',
  'toast.failedDeleteAutomation': 'Failed to delete automation',
  'toast.webhookReplayCompleted': 'Webhook replay completed',
  'toast.replayFailed': 'Replay failed: {message}',
  'toast.failedOpenFolderPicker': 'Failed to open folder picker',
  'toast.failedCreateWorkspace': 'Failed to create workspace',
  'toast.workspaceReconnected': 'Workspace reconnected',
  'toast.patternCopied': 'Pattern copied to clipboard',
  'toast.failedCopyPattern': 'Failed to copy pattern',

  // ── Send to workspace ─────────────────────────────────────────
  'sendTo.session': 'session',
  'sendTo.sessions': 'sessions',
  'sendTo.sendingTo': 'Sending {count} {label} to {target}...',
  'sendTo.sentTo': 'Sent {count} {label} to {target}',
  'sendTo.open': 'Open',
  'sendTo.failedSend': 'Failed to send {label}',

  // ── Edit popover placeholders ─────────────────────────────────
  'editPopover.addSourcePlaceholder': 'What would you like to connect?',
  'editPopover.addSourceExample': 'Connect to my Craft space',
  'editPopover.addSourceApiPlaceholder': 'What API would you like to connect?',
  'editPopover.addSourceApiExample': 'Connect to the OpenAI API',
  'editPopover.addSourceMcpPlaceholder': 'What MCP server would you like to connect?',
  'editPopover.addSourceMcpExample': 'Connect to Linear',
  'editPopover.addSourceLocalPlaceholder': 'What folder would you like to connect?',
  'editPopover.addSourceLocalExample': 'Connect to my Obsidian vault',
  'editPopover.addSkillPlaceholder': 'What should I learn to do?',
  'editPopover.addSkillExample': 'Review PRs following our code standards',
  'editPopover.placeholderEg': '{placeholder}, e.g., "{example}"',

  // ── Crash fallback ────────────────────────────────────────────
  'crash.somethingWentWrong': 'Something went wrong',
  'crash.restartApp': 'Please restart the app. The error has been reported.',
  'crash.reloadPage': 'Please reload the page. The error has been reported.',
  'crash.reload': 'Reload',
} as const

/** The shape of any translation dictionary — keys must match, values are strings */
export type TranslationKeys = { readonly [K in keyof typeof en]: string }
export type TranslationKey = keyof typeof en

export default en
